/**
 * Minimal admin-API helper. Used by login, /me, setup, and the SAML status
 * probe before Refine's dataProvider takes over for authenticated pages.
 */

export interface MeResponse {
  username: string | null;
  role: string | null;
  is_superadmin?: boolean;
  status?: string;
  /** Whether a local password is set (drives the change-password form). */
  has_password?: boolean;
  /** Capabilities the session holds — drives the menu-less scoped shell. */
  capabilities?: string[];
}

export interface OidcStatus {
  enabled: boolean;
}

/** /api/node-health — DR-node liveness (palapian-control). monitored=false on standalone installs. */
export interface NodeHealth {
  monitored: boolean;
  reachable?: boolean;
  node?: string | null;
  role?: string | null; // ACTIVE | STANDBY | ERROR
  live?: boolean | null;
  maintenance?: boolean | null;
  status?: string | null;
}

export interface AdminUserRow {
  id: number;
  username: string;
  role: string | null;
  status: string;
  oidc_subject: string | null;
  email: string | null;
  has_password: boolean;
  barcode_count: number;
  created_at: string;
  updated_at: string;
}

export interface AdminUserCreated {
  user: AdminUserRow;
  /** The generated login barcode value (render as a QR), or null. */
  barcode: string | null;
}

export interface AdminBarcodeRow {
  id: number;
  barcode: string;
  kind: string; // 'login' | 'api'
  comment: string | null;
  valid_from: string | null;
  valid_until: string | null;
  disabled_at: string | null;
  last_used_at: string | null;
}

export interface OidcConfigData {
  enabled: boolean;
  issuer: string | null;
  client_id: string | null;
  has_client_secret: boolean;
  confidential: boolean;
  scopes: string;
  username_claim: string;
  email_claim: string;
  role_claim: string | null;
  role_map: Record<string, string>;
  session_ttl_days: number;
  rp_logout_enabled: boolean;
  last_test_at: string | null;
  last_test_status: string | null;
}

export interface OidcConfigPatch {
  enabled?: boolean;
  issuer?: string;
  client_id?: string;
  client_secret?: string;
  confidential?: boolean;
  scopes?: string;
  username_claim?: string;
  email_claim?: string;
  role_claim?: string;
  role_map?: Record<string, string>;
  session_ttl_days?: number;
  rp_logout_enabled?: boolean;
}

export interface OidcTestResult {
  ok: boolean;
  issuer: string | null;
  error: string | null;
  authorization_endpoint: string | null;
  token_endpoint: string | null;
  userinfo_endpoint: string | null;
  jwks_uri: string | null;
  end_session_endpoint: string | null;
  signing_keys: number | null;
  scopes_supported: string[] | null;
  claims_supported: string[] | null;
  redirect_uri: string | null;
  login_attempted: boolean;
  login_ok: boolean | null;
  login_error: string | null;
  login_claims: Record<string, unknown> | null;
  resolved_role: string | null;
  tested_at: string;
}

export interface RbacCapabilityRow {
  key: string;
  label: string;
  roles: string[];
}

export interface RbacMatrix {
  roles: string[];
  capabilities: RbacCapabilityRow[];
}

/**
 * Convert a FastAPI ``detail`` payload into a human-readable string.
 *
 * FastAPI's 422 validation errors arrive as an array of
 * ``{loc, msg, type, input?}`` objects — naive ``String(detail)`` on
 * that yields ``"[object Object]"``. This helper flattens each entry
 * to ``"loc.path: msg"`` and joins them with ``"; "``. Strings pass
 * through; single-object detail payloads (custom ``HTTPException``
 * bodies like ``{error: "stocktake_in_progress", ...}``) are
 * JSON-stringified so the surface still shows the diagnostic instead
 * of the useless ``"[object Object]"``.
 */
function formatDetail(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (e && typeof e === "object") {
          const obj = e as { loc?: unknown; msg?: unknown };
          const loc = Array.isArray(obj.loc) ? obj.loc.join(".") : "";
          const msg =
            typeof obj.msg === "string" ? obj.msg : "validation error";
          return loc ? `${loc}: ${msg}` : msg;
        }
        return String(e);
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }
  return String(detail ?? "");
}

async function requestJson<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  let body = init?.body;
  if (init?.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(init.json);
  }
  const method = (init?.method ?? "GET").toUpperCase();
  const fullPath = `/api${path}`;
  const response = await fetch(fullPath, {
    credentials: "same-origin",
    ...init,
    headers,
    body,
  });
  const text = await response.text();
  let data: unknown = null;
  try {
    data = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    data = { detail: text.slice(0, 200) };
  }
  if (!response.ok) {
    const detailRaw =
      data && typeof data === "object" && "detail" in data
        ? (data as { detail: unknown }).detail
        : response.statusText;
    const detail = formatDetail(detailRaw);
    throw new ApiError(response.status, detail, method, fullPath, data);
  }
  return data as T;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
    public readonly method: string = "GET",
    public readonly url: string = "",
    public readonly body: unknown = null,
  ) {
    super(`${method} ${url} → HTTP ${status}: ${detail}`);
  }

  /** The structured backend error code (the ``error`` field of the JSON body),
   * or null — so callers can branch on a stable code for reason-specific copy
   * instead of the raw HTTP-debug message. Reads both the ``{error}`` envelope
   * (settle/orchestrator routes) and ``{detail:{error}}`` (manual create). */
  get errorCode(): string | null {
    const b = this.body;
    if (b && typeof b === "object") {
      const top = (b as { error?: unknown }).error;
      if (typeof top === "string") return top;
      const d = (b as { detail?: unknown }).detail;
      if (d && typeof d === "object") {
        const e = (d as { error?: unknown }).error;
        if (typeof e === "string") return e;
      }
    }
    return null;
  }
}

export interface UserRow {
  // admin user_identity row id when the user has actual enrichment
  // saved (real_name, custom user_type, notes); null when the row
  // only exists in strichliste so far. The next edit auto-creates
  // the admin row via PATCH /users/by-strichliste/{id}.
  id: number | null;
  strichliste_user_id: number;
  email: string | null;
  name: string;
  real_name: string | null;
  // Always "regular" or "guest" for non-bank rows: when no admin row
  // exists, the server derives the type from whether strichliste
  // carries an email. bank is filtered out server-side.
  user_type: "regular" | "guest" | "bank";
  disabled: boolean;
  active: boolean;
  balance_cents: number | null;
  public_note: string | null;
  internal_note: string | null;
  // ERP (Snipe-IT) email override; admin-only, never on public endpoints.
  erp_email: string | null;
  created: string | null;
  updated: string | null;
  last_activity: string | null;
  /** Lifetime cost-of-goods for this user's article purchases (Σ qty ×
   *  buy-price-at-purchase). Only present when the list was fetched with
   *  ``include_cogs``; ``0`` = no priced purchases. */
  cost_of_goods_cents: number | null;
}

export interface UserPage {
  items: UserRow[];
  total: number;
  page: number;
  page_size: number;
}

// ---- Transaction history (user detail page) ----

export interface TransactionRow {
  id: number;
  user_id: number;
  created: string;
  amount: number;
  quantity: number | null;
  article_id: number | null;
  article_name: string | null;
  comment: string | null;
  recipient_transaction_id: number | null;
  sender_transaction_id: number | null;
  // Set when this row has been corrected (display strikethrough).
  corrected_at_correction_id: number | null;
  // Set when this row IS the counter-transfer for some correction.
  is_counter_for_correction_id: number | null;
}

export interface TransactionList {
  items: TransactionRow[];
  total: number;
}

export interface BuyArticleResult {
  transaction_id: number;
  article_id: number | null;
  article_name: string | null;
  amount_cents: number; // signed; negative = the price debited
  balance_cents: number | null; // user balance after the buy, if returned
}

export interface UndoTransactionResult {
  ok: boolean;
  transaction_id: number;
  balance_cents: number | null;
}

// ---- Corrections ----

export interface CorrectionReasonRow {
  id: number;
  label: string;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CorrectionRow {
  id: number;
  receipt_no: string;
  original_transaction_id: number;
  counter_transaction_id: number | null;
  strichliste_user_id: number;
  amount_cents: number;
  reason_id: number | null;
  reason_label: string;
  comment: string | null;
  operator_id: number | null;
  status: "pending" | "completed" | "failed" | "voided";
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface BatchItemIn {
  original_transaction_id: number;
  reason_id: number;
}

export interface BatchItemResult {
  original_transaction_id: number;
  correction_id: number | null;
  receipt_no: string | null;
  counter_transaction_id: number | null;
  amount_cents: number | null;
  status: "completed" | "failed" | "validation_failed";
  error: string | null;
}

export interface BatchResponse {
  items: BatchItemResult[];
  summary: Record<string, number>;
}

export interface DryRunRow {
  line_number: number;
  email: string;
  nickname: string;
  real_name: string | null;
  disabled: boolean;
  parser_errors: string[];
  // Non-blocking date-quality flags (swap detected, un-parseable date).
  // Row still commits with the offending date column(s) nulled out;
  // dry-run sorts warning-bearing rows to the top of the preview.
  parser_warnings: string[];
  parser_notes: string[];
  // ISO yyyy-mm-dd strings; null when the column wasn't mapped or no
  // source row supplied a value.
  arrival_date: string | null;
  departure_date: string | null;
  // Volunteer days detected on this row (ISO dates); empty when the grid
  // isn't mapped or the row has no V cells. (add-volunteer-compensation)
  volunteer_dates?: string[];
  // Free-form per-row meta (e.g. candidate_strichliste_user_ids for the
  // ambiguous-email refusal case). Empty object when nothing to report.
  meta: Record<string, unknown>;
  action: "create" | "adopt" | "update" | "unchanged" | "filtered" | "conflict" | "skip";
  detail: string | null;
  existing_user_identity_id: number | null;
  existing_strichliste_user_id: number | null;
  // For UPDATE rows: which cells will actually change vs the existing
  // admin record. Subset of "real_name" / "arrival_date" /
  // "departure_date". Empty for non-UPDATE rows.
  changed_fields?: string[];
}

export interface DryRunResponse {
  rows: DryRunRow[];
  summary: Record<string, number>;
}

export interface CommitResultRow {
  line_number: number;
  action: string;
  success: boolean;
  email: string;
  nickname: string;
  real_name: string | null;
  error: string | null;
  user_identity_id: number | null;
  strichliste_user_id: number | null;
}

export interface CommitResponse {
  results: CommitResultRow[];
  summary: Record<string, number>;
}

export interface ColumnMapping {
  email: string;
  nickname: string;
  real_name: string[];
  filter_column?: string | null;
  // Festival roster import (§13). Optional — when set, the importer
  // parses the column as DD.MM.YYYY / YYYY-MM-DD / DD/MM/YYYY and
  // writes the resulting date onto user_identity.arrival_date /
  // departure_date. Blank cells don't clear existing dates.
  arrival_date_column?: string | null;
  departure_date_column?: string | null;
  // Timetable-grid columns (Wanderzirkus crew sheet shape). Auto-
  // detected by the inspector when a long run of German weekday-date
  // headers appears in a row. When present, the parser scans these
  // headers in order and derives arrival = first non-empty cell's
  // date, departure = last non-empty cell's date. Coexists with the
  // manual column pickers above; if both produce a date and disagree,
  // the manual columns win and the row carries a
  // grid_manual_date_mismatch warning.
  timetable_grid_columns?: string[];
  // Form-submission timestamp column (CSV col 0 in the demo export).
  // Used by the consolidator to order same-email rows newest-first so
  // the latest valid arrival/departure value wins.
  submission_timestamp_column?: string | null;
  // Festival/event calendar year for the timetable-grid header dates
  // (add-volunteer-compensation). The grid headers carry no year, so this
  // pins V-days AND arrival/departure to the event year even when the CSV is
  // imported in a later calendar year. The inspector defaults it to the
  // current year; sent on both dry-run and commit.
  event_year?: number | null;
}

export interface InspectResponse {
  headers: string[];
  preview: string[][];
  suggested_mapping: ColumnMapping | null;
  rationale: Record<string, string>;
}

async function uploadCsv<T>(
  path: string,
  csv: File,
  options: {
    actions?: { line_number: number; action: string }[];
    mapping?: ColumnMapping;
  } = {},
): Promise<T> {
  const fd = new FormData();
  fd.append("file", csv);
  if (options.actions) fd.append("actions", JSON.stringify(options.actions));
  if (options.mapping) fd.append("mapping", JSON.stringify(options.mapping));
  const fullPath = `/api${path}`;
  const r = await fetch(fullPath, {
    method: "POST",
    credentials: "same-origin",
    body: fd,
  });
  const text = await r.text();
  let data: unknown = null;
  try {
    data = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    data = { detail: text.slice(0, 200) };
  }
  if (!r.ok) {
    const detailRaw =
      data && typeof data === "object" && "detail" in data
        ? (data as { detail: unknown }).detail
        : r.statusText;
    const detail = formatDetail(detailRaw);
    throw new ApiError(r.status, detail, "POST", fullPath, data);
  }
  return data as T;
}

export interface ArticleBarcode {
  id: number;
  barcode: string;
}

export interface ArticleRow {
  id: number;
  name: string;
  sell_price_cents: number;
  active: boolean;
  usage_count: number;
  chain_root_id: number;
  is_chain_root: boolean;
  barcodes: ArticleBarcode[];
  unit_size: string | null;
  unit_type: string | null;
  parser_confidence: string | null;
  unit_size_override: string | null;
  unit_type_override: string | null;
  pack_size: number | null;
  /** Per-article cup pfand override: true=always / false=never / null=inherit. */
  cup_required_override: boolean | null;
  buy_price_cents: number | null;
  product_group_id: number | null;
  product_group_name: string | null;
  margin_cents: number | null;
  // Pfand FKs into ``pfand_value`` (catalog right pane Pfand section).
  // null = no deposit on this article.
  pfand_value_id: number | null;
  crate_pfand_value_id: number | null;
  // On-site (POS) bottle pfand — auto-adds a deposit line at the POS. null = none.
  onsite_pfand_value_id: number | null;
  // Optional POS sell price (cents). null = the POS uses sell_price_cents.
  pos_price_cents: number | null;
  // VAT rate id (FK into vat_rate); defaults to 1 (19%). Prices stay gross.
  vat_rate_id: number;
  // Category (resolved from article_metadata.category_id). Drives the POS
  // "hide from main POS" filter; null = uncategorised.
  category_id: number | null;
  category_name: string | null;
}

export interface BuyPriceHistoryRow {
  id: number;
  buy_price_cents: number;
  supplier: string | null;
  effective_from: string;
  effective_to: string | null;
}

export interface ArticleDetail extends ArticleRow {
  buy_price_history: BuyPriceHistoryRow[];
  precursor_id: number | null;
}

export interface ChainEntry {
  id: number;
  name: string;
  sell_price_cents: number;
  active: boolean;
  usage_count: number;
  created: string | null;
  role: "root" | "intermediate" | "leaf" | "root_and_leaf";
  precursor_id: number | null;
}

export interface ChainRow {
  chain_root_id: number;
  current_leaf_id: number;
  name: string;
  sell_price_cents: number;
  active: boolean;
  chain: ChainEntry[];
  barcodes: ArticleBarcode[];
  unit_size: string | null;
  unit_type: string | null;
  parser_confidence: string | null;
  unit_size_override: string | null;
  unit_type_override: string | null;
  pack_size: number | null;
  /** Per-article cup pfand override: true=always / false=never / null=inherit. */
  cup_required_override: boolean | null;
  buy_price_cents: number | null;
  product_group_id: number | null;
  product_group_name: string | null;
  category_id: number | null;
  category_name: string | null;
  margin_cents: number | null;
  // Pfand FKs into ``pfand_value`` — see ArticleRow for context.
  pfand_value_id: number | null;
  crate_pfand_value_id: number | null;
  onsite_pfand_value_id: number | null;
  pos_price_cents: number | null;
  vat_rate_id: number;
  refilled_qty: number;
  sold_qty: number;
  expected_qty: number;
  counted_qty: number | null;
  mismatch_qty: number | null;
}

export interface ConsumptionSeriesPoint {
  /** ``YYYY-MM-DD``. */
  date: string;
  /** Units sold on that day (zero-filled if no sales). */
  qty: number;
}

export interface ConsumptionSeriesResponse {
  series: ConsumptionSeriesPoint[];
}

export type LedgerSource =
  | "procurement"
  | "sale"
  | "stocktake"
  | "stock_loss"
  | "adjustment"
  | "correction"
  | "stocktake_reverted";

export interface LedgerRow {
  source: LedgerSource;
  id: number;
  timestamp: string;
  qty: number | null;
  pfand_cents: number | null;
  value_cents: number | null;
  label: string;
  buyer_id: number | null;
  buyer_name: string | null;
  buyer_count: number | null;
  link: { type: string; id: number } | null;
}

export interface LedgerSummary {
  revenue_cents: number;
  units_sold: number;
  goods_cost_cents: number;
  pfand_in_cents: number;
  stock_loss_cents: number;
  corrections_cents: number;
  profit_cents: number;
}

export interface ArticleLedgerResponse {
  chain_root_id: number;
  aggregate: "daily" | "granular";
  rows: LedgerRow[];
  summary: LedgerSummary;
  total_sales_count: number;
  sales_truncated: boolean;
}

export interface PfandValueRow {
  id: number;
  label: string;
  cents: number;
  kind: "german" | "onsite";
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VatRateRow {
  id: number;
  name: string;
  percent: number;
  system_seeded: boolean;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CategoryRow {
  id: number;
  name: string;
  sort_order: number;
  active: boolean;
  // POS: articles in a cup-flagged category auto-bundle a cup at the till.
  cup_required: boolean;
  // Direct-sale visibility: hide a filler-ingredient category from the POS
  // sell screen / the kiosk while keeping it usable in recipes + at stocktake.
  hide_from_pos: boolean;
  hide_from_kiosk: boolean;
  // Marks the category as merchandise (textiles). Cross-cuts the VAT
  // breakdown on the settlement invoice.
  is_merch: boolean;
}

export interface ProductGroupRow {
  id: number;
  name: string;
  category_id: number;
  category_name: string;
  sort_order: number;
  active: boolean;
  member_count: number;
  unit_type_warning: string | null;
}

export type RevenueWindow =
  | "alltime"
  | "30d"
  | "7d"
  | "refill"
  | "stocktake"
  | "custom";

export interface ArticleMetricsResponse {
  revenue_cents: number;
  sales_count: number;
  last_sold_at: string | null;
  window_resolved: { kind: string; from: string | null; to: string | null };
}

export interface CatalogRevenueResponse {
  window: string;
  from_: string | null;
  to: string | null;
  articles: {
    article_chain_root_id: number;
    revenue_cents: number;
    profit_cents: number;
    units_sold: number;
  }[];
  groups: {
    product_group_id: number;
    revenue_cents: number;
    profit_cents: number;
    units_sold: number;
    anchor: string | null;
  }[];
  categories: {
    category_id: number;
    revenue_cents: number;
    profit_cents: number;
    units_sold: number;
    anchor: string | null;
  }[];
}

export interface UncategorisedCountResponse {
  count: number;
  as_of: string;
}

// Mirrors ``app.routes.catalog`` Pydantic models for the
// ``GET /api/catalog/stock`` endpoint. Keep field names + nullability
// in lockstep with the backend models — the parser is positional via
// JSON keys so any drift surfaces as undefined fields at runtime.
export interface StockArticleEntry {
  article_chain_root_id: number;
  current_stock: number;
  /** Refilled in selected window (lifetime if window is "alltime"). */
  refilled_qty: number;
  /** Sold in selected window. */
  sold_qty: number;
  counted_qty: number | null;
  /** When the latest finalized count was taken — for the "last counted DD.MM
   *  · moved since" freshness tooltip. ``null`` when never counted. */
  counted_at: string | null;
  /** The discrepancy DISCOVERED AT the last count (counted − the book snapshot
   *  then), frozen until the next count — NOT a live drift. */
  mismatch_qty: number | null;
  /** mismatch_qty / max(|expected_at_finalize|, 1); null when no count yet. */
  mismatch_pct: number | null;
  days_of_cover: number | null;
  pfand_total_cents: number;
  /** Lifetime last refill timestamp (not window-bound). */
  last_refill_at: string | null;
  /** Cumulative ``Σ stock_adjustment.delta_qty`` over all time.
   *  ``null`` when no adjustment rows exist; ``0`` when rows exist but
   *  net to zero. */
  total_mismatch_qty: number | null;
  /** Stock right after this article's last received refill
   *  (current_stock + sold-since-that-refill); ``null`` when never
   *  delivered. Powers the low-stock highlight's "since last delivery"
   *  baseline (the default baseline is crate size = pack_size). */
  since_delivery_baseline_qty: number | null;
}

export interface StockGroupEntry {
  product_group_id: number;
  current_stock: number;
  refilled_qty: number;
  sold_qty: number;
  pfand_total_cents: number;
}

export interface StockCategoryEntry {
  category_id: number;
  current_stock: number;
  refilled_qty: number;
  sold_qty: number;
  pfand_total_cents: number;
}

export interface CatalogStockResponse {
  window: string;
  from_: string | null;
  to: string | null;
  articles: StockArticleEntry[];
  groups: StockGroupEntry[];
  categories: StockCategoryEntry[];
}

// Volunteer compensation (add-volunteer-compensation).
export interface VolunteerDayRow {
  id: number;
  work_date: string;
  paid: boolean;
  is_today: boolean;
  is_future: boolean;
  claim_id: number | null;
  expense_id: number | null;
  credit_tx_id: number | null;
  value_cents: number | null;
}
export interface VolunteerDaysSummary {
  owed_count: number;
  owed_value_cents: number;
  paid_count: number;
  paid_value_cents: number;
  future_count: number;
}
export interface VolunteerDaysResponse {
  days: VolunteerDayRow[];
  rate_cents: number;
  summary: VolunteerDaysSummary;
}
export interface VolunteerPreviewDay {
  id: number;
  work_date: string;
  is_today: boolean;
}
export interface VolunteerPreview {
  past_eligible_days: number;
  today_eligible: boolean;
  rate_cents: number;
  debt_cents: number;
  balance_cents: number;
  payable_days_incl_today: number;
  payable_days_excl_today: number;
  future_eligible_days: number;
  days: VolunteerPreviewDay[];
}
export interface VolunteerClaimResult {
  paid_cents: number;
  paid_day_ids: number[];
  claim_id: number | null;
  new_balance_cents: number | null;
  rate_cents: number;
  eligible_day_count: number;
}
export interface VolunteerOwedCount {
  strichliste_user_id: number;
  owed_count: number;
  owed_value_cents: number;
}

export const api = {
  me: () => requestJson<MeResponse>("/auth/me"),
  loginPassword: (username: string, password: string) =>
    requestJson<{ ok: boolean }>("/auth/login", {
      method: "POST",
      json: { username, password },
    }),
  loginBarcode: (barcode: string) =>
    requestJson<{ ok: boolean }>("/auth/login", {
      method: "POST",
      json: { barcode },
    }),
  logout: () => requestJson<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  changePassword: (payload: { current_password?: string; new_password: string }) =>
    requestJson<{ ok: boolean }>("/auth/change-password", {
      method: "POST",
      json: payload,
    }),
  setupStatus: () => requestJson<{ first_launch: boolean }>("/setup"),
  createFirstOwner: (payload: {
    username: string;
    password: string;
    email?: string;
    real_name?: string;
  }) =>
    requestJson<{ id: number; username: string; role: string }>(
      "/setup/first-owner",
      { method: "POST", json: payload },
    ),
  oidcStatus: () => requestJson<OidcStatus>("/auth/oidc/status"),
  nodeHealth: () => requestJson<NodeHealth>("/node-health"),
  // --- OIDC config (Owner) ---
  getOidcConfig: () => requestJson<OidcConfigData>("/oidc-config"),
  patchOidcConfig: (payload: OidcConfigPatch) =>
    requestJson<OidcConfigData>("/oidc-config", { method: "PATCH", json: payload }),
  testOidcConnection: (creds?: { username: string; password: string }) =>
    requestJson<OidcTestResult>("/oidc-config/test", {
      method: "POST",
      json: creds ?? {},
    }),
  // --- Admin-user management (Owner) ---
  listAdminUsers: (statusFilter?: string) =>
    requestJson<AdminUserRow[]>(
      `/admin-users${statusFilter ? `?status_filter=${statusFilter}` : ""}`,
    ),
  createAdminUser: (payload: {
    username: string;
    role: string;
    generate_barcode: boolean;
  }) =>
    requestJson<AdminUserCreated>("/admin-users", {
      method: "POST",
      json: payload,
    }),
  approveAdminUser: (id: number, role: string) =>
    requestJson<AdminUserRow>(`/admin-users/${id}/approve`, {
      method: "POST",
      json: { role },
    }),
  changeAdminUserRole: (id: number, role: string) =>
    requestJson<AdminUserRow>(`/admin-users/${id}`, {
      method: "PATCH",
      json: { role },
    }),
  disableAdminUser: (id: number) =>
    requestJson<AdminUserRow>(`/admin-users/${id}/disable`, { method: "POST" }),
  enableAdminUser: (id: number) =>
    requestJson<AdminUserRow>(`/admin-users/${id}/enable`, { method: "POST" }),
  revokeAdminUserSessions: (id: number) =>
    requestJson<{ revoked: number }>(`/admin-users/${id}/revoke-sessions`, {
      method: "POST",
    }),
  setAdminUserPassword: (id: number, password: string) =>
    requestJson<AdminUserRow>(`/admin-users/${id}/set-password`, {
      method: "POST",
      json: { password },
    }),
  clearAdminUserPassword: (id: number) =>
    requestJson<AdminUserRow>(`/admin-users/${id}/clear-password`, {
      method: "POST",
    }),
  unlinkAdminUserOidc: (id: number) =>
    requestJson<AdminUserRow>(`/admin-users/${id}/unlink-oidc`, {
      method: "POST",
    }),
  listAdminUserBarcodes: (id: number) =>
    requestJson<AdminBarcodeRow[]>(`/admin-users/${id}/barcodes`),
  addAdminUserBarcode: (
    id: number,
    payload: {
      barcode?: string;
      generate?: boolean;
      kind?: string;
      comment?: string;
      valid_from?: string;
      valid_until?: string;
    },
  ) =>
    requestJson<AdminBarcodeRow>(`/admin-users/${id}/barcodes`, {
      method: "POST",
      json: payload,
    }),
  deleteAdminUserBarcode: (id: number, barcodeId: number) =>
    requestJson<{ ok: boolean }>(`/admin-users/${id}/barcodes/${barcodeId}`, {
      method: "DELETE",
    }),
  disableAdminUserBarcode: (id: number, barcodeId: number) =>
    requestJson<AdminBarcodeRow>(
      `/admin-users/${id}/barcodes/${barcodeId}/disable`,
      { method: "POST" },
    ),
  enableAdminUserBarcode: (id: number, barcodeId: number) =>
    requestJson<AdminBarcodeRow>(
      `/admin-users/${id}/barcodes/${barcodeId}/enable`,
      { method: "POST" },
    ),
  // --- Read-only role-capability matrix (Viewer+) ---
  rbacMatrix: () => requestJson<RbacMatrix>("/rbac/matrix"),
  listUsers: (
    params: {
      user_type?: string;
      show_vouchers?: boolean;
      q?: string;
      sort?:
        | "name"
        | "balance"
        | "created"
        | "updated"
        | "email"
        | "real_name"
        | "last_activity"
        | "cost";
      sort_dir?: "asc" | "desc";
      disabled?: boolean;
      status?: "active" | "inactive" | "disabled";
      include_cogs?: boolean;
      page?: number;
      page_size?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.user_type) qs.set("user_type", params.user_type);
    if (params.show_vouchers) qs.set("show_vouchers", "true");
    if (params.q) qs.set("q", params.q);
    if (params.sort) qs.set("sort", params.sort);
    if (params.sort_dir) qs.set("sort_dir", params.sort_dir);
    if (params.disabled !== undefined) qs.set("disabled", String(params.disabled));
    if (params.status) qs.set("status", params.status);
    if (params.include_cogs) qs.set("include_cogs", "true");
    if (params.page) qs.set("page", String(params.page));
    if (params.page_size) qs.set("page_size", String(params.page_size));
    const s = qs.toString();
    return requestJson<UserPage>(`/users${s ? `?${s}` : ""}`);
  },
  getUserByStrichliste: (strichlisteUserId: number) =>
    requestJson<UserRow>(`/users/by-strichliste/${strichlisteUserId}`),
  createUser: (payload: {
    email?: string;
    name: string;
    real_name?: string;
    user_type?: "regular" | "guest";
    // When set, adopt the existing strichliste user rather than
    // create a new one. Email + name come from strichliste in that
    // case; the server ignores the email/name fields above.
    strichliste_user_id?: number;
  }) =>
    requestJson<UserRow>("/users", {
      method: "POST",
      json: { ...payload, user_type: payload.user_type ?? "regular" },
    }),
  updateUser: (
    strichlisteUserId: number,
    payload: {
      email?: string;
      name?: string;
      real_name?: string;
      user_type?: "regular" | "guest";
      public_note?: string;
      internal_note?: string;
      erp_email?: string | null;
    },
  ) =>
    requestJson<UserRow>(
      `/users/by-strichliste/${strichlisteUserId}`,
      { method: "PATCH", json: payload },
    ),
  disableUser: (strichlisteUserId: number) =>
    requestJson<UserRow>(
      `/users/by-strichliste/${strichlisteUserId}/disable`,
      { method: "POST" },
    ),
  reenableUser: (strichlisteUserId: number) =>
    requestJson<UserRow>(
      `/users/by-strichliste/${strichlisteUserId}/reenable`,
      { method: "POST" },
    ),
  listUserTransactions: (strichlisteUserId: number) =>
    requestJson<TransactionList>(`/users/${strichlisteUserId}/transactions`),
  // Volunteer compensation (add-volunteer-compensation).
  getVolunteerDays: (strichlisteUserId: number) =>
    requestJson<VolunteerDaysResponse>(
      `/users/${strichlisteUserId}/volunteer-days`,
    ),
  previewVolunteerClaim: (strichlisteUserId: number) =>
    requestJson<VolunteerPreview>(`/volunteer/preview/${strichlisteUserId}`),
  /** Pay out V-days against the user's debt. ``max_days`` lets the operator cap
   * how many days actually pay out (null = as many as the debt allows). Office+. */
  claimVolunteer: (payload: {
    strichliste_user_id: number;
    include_today: boolean;
    max_days?: number | null;
    include_future?: boolean;
  }) =>
    requestJson<VolunteerClaimResult>(`/volunteer/claim`, {
      method: "POST",
      json: payload,
    }),
  reverseVolunteer: (payload: { strichliste_user_id: number; claim_id: number }) =>
    requestJson<{ reversed_days: number; amount_cents: number }>(
      `/volunteer/reverse`,
      { method: "POST", json: payload },
    ),
  getVolunteerOwedCounts: () =>
    requestJson<{ counts: VolunteerOwedCount[] }>(`/volunteer/owed-counts`),
  /** Buy an article for a user (kiosk-style: strichliste prices it from the id).
   * ``quantity`` (optional, 1..99) — one transaction row is written server-side
   * regardless of N (strichliste multiplies amount server-side). Returns the
   * new transaction id (for undo). Office+. */
  buyArticleForUser: (
    strichlisteUserId: number,
    articleId: number,
    quantity?: number,
  ) =>
    requestJson<BuyArticleResult>(`/users/${strichlisteUserId}/buy`, {
      method: "POST",
      json: quantity && quantity > 1
        ? { article_id: articleId, quantity }
        : { article_id: articleId },
    }),
  /** Undo (soft-delete) a transaction, reverting the balance. Office+. */
  undoUserTransaction: (strichlisteUserId: number, transactionId: number) =>
    requestJson<UndoTransactionResult>(
      `/users/${strichlisteUserId}/transactions/${transactionId}`,
      { method: "DELETE" },
    ),
  listCorrectionReasons: (active?: boolean) => {
    const qs = active === undefined ? "" : `?active=${active}`;
    return requestJson<CorrectionReasonRow[]>(`/correction-reasons${qs}`);
  },
  createCorrectionReason: (label: string, sort_order = 0) =>
    requestJson<CorrectionReasonRow>("/correction-reasons", {
      method: "POST",
      json: { label, sort_order },
    }),
  updateCorrectionReason: (
    id: number,
    payload: { label?: string; sort_order?: number; active?: boolean },
  ) =>
    requestJson<CorrectionReasonRow>(`/correction-reasons/${id}`, {
      method: "PATCH",
      json: payload,
    }),
  archiveCorrectionReason: (id: number) =>
    requestJson<CorrectionReasonRow>(`/correction-reasons/${id}`, { method: "DELETE" }),
  reorderCorrectionReasons: (orderedIds: number[]) =>
    requestJson<CorrectionReasonRow[]>(`/correction-reasons/reorder`, {
      method: "PUT",
      json: { ordered_ids: orderedIds },
    }),
  commitCorrectionBatch: (payload: {
    batch_comment?: string;
    items: BatchItemIn[];
    return_to_stock?: boolean;
  }) =>
    requestJson<BatchResponse>("/corrections/batch", {
      method: "POST",
      json: payload,
    }),
  patchCorrection: (
    id: number,
    payload: { reason_id?: number; comment?: string },
  ) =>
    requestJson<CorrectionRow>(`/corrections/${id}`, {
      method: "PATCH",
      json: payload,
    }),
  importInspect: (csv: File) =>
    uploadCsv<InspectResponse>("/users/import/inspect", csv),
  importDryRun: (csv: File, mapping?: ColumnMapping) =>
    uploadCsv<DryRunResponse>("/users/import/dry-run", csv, { mapping }),
  importCommit: (
    csv: File,
    options: {
      actions?: { line_number: number; action: string }[];
      mapping?: ColumnMapping;
    } = {},
  ) => uploadCsv<CommitResponse>("/users/import/commit", csv, options),

  // ---- Articles ----
  /** Default chain-grouped view (one row per product chain root). */
  listArticleChains: () => requestJson<ChainRow[]>("/articles"),
  /** Flat leaf view — every strichliste article id matching ``active``. */
  listArticles: (params: { active?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.active !== undefined) q.set("active", String(params.active));
    q.set("view", "leaf");
    const qs = q.toString();
    return requestJson<ArticleRow[]>(`/articles?${qs}`);
  },
  getArticle: (id: number) => requestJson<ArticleDetail>(`/articles/${id}`),
  createArticle: (payload: {
    name: string;
    sell_price_cents: number;
    category_id: number;
    product_group_id?: number | null;
    barcode?: string;
    unit_size_override?: string | null;
    unit_type_override?: string | null;
    pack_size?: number | null;
  }) => requestJson<ArticleRow>("/articles", { method: "POST", json: payload }),
  /**
   * Catalog drag-drop reassignment. Server derives category from the
   * provided group; null group + non-null category = "Ungrouped in
   * category X"; both null is rejected.
   */
  assignArticleGroup: (
    id: number,
    payload: {
      product_group_id: number | null;
      category_id: number | null;
    },
  ) =>
    requestJson<ArticleRow>(`/articles/${id}/group`, {
      method: "PATCH",
      json: payload,
    }),
  getArticleMetrics: (
    id: number,
    params: { window?: RevenueWindow; from?: string; to?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.window) q.set("window", params.window);
    if (params.from) q.set("from_", params.from);
    if (params.to) q.set("to", params.to);
    const qs = q.toString();
    return requestJson<ArticleMetricsResponse>(
      `/articles/${id}/metrics${qs ? `?${qs}` : ""}`,
    );
  },
  updateArticle: (id: number, payload: { name?: string; sell_price_cents?: number }) =>
    requestJson<ArticleRow>(`/articles/${id}`, { method: "PATCH", json: payload }),
  deleteArticle: (id: number) =>
    requestJson<null>(`/articles/${id}`, { method: "DELETE" }),
  addBarcode: (articleId: number, barcode: string) =>
    requestJson<ArticleRow>(`/articles/${articleId}/barcodes`, {
      method: "POST",
      json: { barcode },
    }),
  removeBarcode: (articleId: number, barcodeId: number) =>
    requestJson<null>(`/articles/${articleId}/barcodes/${barcodeId}`, { method: "DELETE" }),
  patchMetadata: (
    articleId: number,
    payload: {
      unit_size_override?: string | null;
      unit_type_override?: string | null;
      /** When set_pack_size is true, pack_size is applied (a value of
       * null clears it); otherwise the field is ignored on the server. */
      pack_size?: number | null;
      set_pack_size?: boolean;
      /** Per-article cup pfand override (three-state). Applied only when
       * set_cup_required_override is true: true=always / false=never /
       * null=inherit the category flag. */
      cup_required_override?: boolean | null;
      set_cup_required_override?: boolean;
    },
  ) =>
    requestJson<ArticleRow>(`/articles/${articleId}/metadata`, {
      method: "PATCH",
      json: payload,
    }),
  /** Catalog right pane Pfand section — set the primary + crate FK pair.
   * Either field accepts ``null`` to clear that side. Server validates
   * the FKs exist (400 on unknown id). */
  assignArticlePfand: (
    articleId: number,
    payload: {
      pfand_value_id: number | null;
      crate_pfand_value_id: number | null;
    },
  ) =>
    requestJson<ArticleRow>(`/articles/${articleId}/pfand`, {
      method: "PATCH",
      json: payload,
    }),
  /** Catalog right pane — set the on-site (POS) bottle pfand (or null to
   * clear). Server rejects a German-kind value (422) and a re-point while the
   * current on-site pfand still has an outstanding liability (409). */
  assignArticleOnsitePfand: (
    articleId: number,
    onsitePfandValueId: number | null,
  ) =>
    requestJson<ArticleRow>(`/articles/${articleId}/onsite-pfand`, {
      method: "PATCH",
      json: { onsite_pfand_value_id: onsitePfandValueId },
    }),
  /** Catalog right pane — set the optional POS sell price in cents (or null
   * to fall back to the kiosk sell_price_cents). */
  assignArticlePosPrice: (articleId: number, posPriceCents: number | null) =>
    requestJson<ArticleRow>(`/articles/${articleId}/pos-price`, {
      method: "PATCH",
      json: { pos_price_cents: posPriceCents },
    }),
  assignArticleVat: (articleId: number, vatRateId: number) =>
    requestJson<ArticleRow>(`/articles/${articleId}/vat`, {
      method: "PATCH",
      json: { vat_rate_id: vatRateId },
    }),
  /** Append a new buy-price row keyed by chain-root id (§8.6 blocker
   * fix + §12 catalog right pane). The backend closes out any
   * currently-open row at ``effective_from``; absent that param it
   * defaults to ``now()``. */
  setArticleBuyPrice: (
    chainRootId: number,
    payload: {
      buy_price_cents: number;
      effective_from?: string;
      supplier?: string | null;
    },
  ) =>
    requestJson<{
      id: number;
      chain_root_id: number;
      buy_price_cents: number;
      supplier: string | null;
      effective_from: string;
      effective_to: string | null;
    }>(`/articles/${chainRootId}/buy-price`, {
      method: "POST",
      json: payload,
    }),
  /** 30-day default daily-qty series for the consumption sparkline.
   * Server zero-fills missing days so the consumer can plot directly. */
  getConsumptionSeries: (chainRootId: number, days = 30) =>
    requestJson<ConsumptionSeriesResponse>(
      `/articles/${chainRootId}/consumption-series?days=${days}`,
    ),
  /** Per-article ledger: unified event feed (procurement / sales /
   *  stocktake / stock-loss / adjustment / correction) + P&L summary.
   *  ``aggregate=daily`` (default) collapses sales per day. */
  getArticleLedger: (
    chainRootId: number,
    params: { aggregate?: "daily" | "granular" } = {},
  ) =>
    requestJson<ArticleLedgerResponse>(
      `/catalog/articles/${chainRootId}/ledger${
        params.aggregate ? `?aggregate=${params.aggregate}` : ""
      }`,
    ),
  /** Group-level consumption series — sums daily qty across every
   *  article in the group. Same shape as the article endpoint. */
  getGroupConsumptionSeries: (groupId: number, days = 30) =>
    requestJson<ConsumptionSeriesResponse>(
      `/product-groups/${groupId}/consumption-series?days=${days}`,
    ),
  /** Category-level consumption series — sums daily qty across every
   *  article in the category (including the un-grouped articles
   *  whose category_id matches). Same shape as the article endpoint. */
  getCategoryConsumptionSeries: (categoryId: number, days = 30) =>
    requestJson<ConsumptionSeriesResponse>(
      `/categories/${categoryId}/consumption-series?days=${days}`,
    ),

  // ---- Pfand values (catalog right pane Pfand dropdowns) ----
  listPfandValues: (
    includeArchived = false,
    kind?: "german" | "onsite",
  ) => {
    const qs = new URLSearchParams();
    if (includeArchived) qs.set("include_archived", "true");
    if (kind) qs.set("kind", kind);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return requestJson<PfandValueRow[]>(`/pfand-values${suffix}`);
  },

  // ---- Categories ----
  listCategories: (includeInactive = false) =>
    requestJson<CategoryRow[]>(
      `/categories${includeInactive ? "?include_inactive=true" : ""}`,
    ),
  createCategory: (payload: { name: string; sort_order?: number }) =>
    requestJson<CategoryRow>("/categories", { method: "POST", json: payload }),
  updateCategory: (
    id: number,
    payload: {
      name?: string;
      sort_order?: number;
      active?: boolean;
      cup_required?: boolean;
      hide_from_pos?: boolean;
      hide_from_kiosk?: boolean;
      is_merch?: boolean;
    },
  ) => requestJson<CategoryRow>(`/categories/${id}`, { method: "PATCH", json: payload }),
  deleteCategory: (id: number) =>
    requestJson<null>(`/categories/${id}`, { method: "DELETE" }),
  reorderCategories: (orderedIds: number[]) =>
    requestJson<CategoryRow[]>(`/categories/reorder`, {
      method: "PUT",
      json: { ordered_ids: orderedIds },
    }),

  // ---- Product groups ----
  listProductGroups: (params: { category_id?: number; include_inactive?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.category_id !== undefined) q.set("category_id", String(params.category_id));
    if (params.include_inactive) q.set("include_inactive", "true");
    const qs = q.toString();
    return requestJson<ProductGroupRow[]>(`/product-groups${qs ? `?${qs}` : ""}`);
  },
  createProductGroup: (payload: {
    name: string;
    category_id: number;
    sort_order?: number;
  }) =>
    requestJson<ProductGroupRow>("/product-groups", {
      method: "POST",
      json: payload,
    }),
  updateProductGroup: (
    id: number,
    payload: {
      name?: string;
      category_id?: number;
      sort_order?: number;
      active?: boolean;
    },
  ) =>
    requestJson<ProductGroupRow>(`/product-groups/${id}`, {
      method: "PATCH",
      json: payload,
    }),
  deleteProductGroup: (id: number) =>
    requestJson<null>(`/product-groups/${id}`, { method: "DELETE" }),
  reorderProductGroups: (categoryId: number, orderedGroupIds: number[]) =>
    requestJson<ProductGroupRow[]>(`/product-groups/reorder`, {
      method: "PUT",
      json: { category_id: categoryId, ordered_group_ids: orderedGroupIds },
    }),

  // ---- Catalog workspace rollups ----
  getCatalogRevenue: (
    params: { window?: RevenueWindow; from?: string; to?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.window) q.set("window", params.window);
    if (params.from) q.set("from_", params.from);
    if (params.to) q.set("to", params.to);
    const qs = q.toString();
    return requestJson<CatalogRevenueResponse>(
      `/catalog/revenue${qs ? `?${qs}` : ""}`,
    );
  },
  getUncategorisedCount: () =>
    requestJson<UncategorisedCountResponse>(`/catalog/uncategorised-count`),
  getCatalogStock: (
    params: { window?: RevenueWindow; from?: string; to?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.window) q.set("window", params.window);
    if (params.from) q.set("from_", params.from);
    if (params.to) q.set("to", params.to);
    const qs = q.toString();
    return requestJson<CatalogStockResponse>(
      `/catalog/stock${qs ? `?${qs}` : ""}`,
    );
  },

  // ---- Refills ----
  listRefills: (limit = 50) => requestJson<RefillRow[]>(`/refills?limit=${limit}`),
  createRefill: (payload: {
    supplier?: string;
    invoice_ref?: string;
    received_at?: string;
    comment?: string;
    lines: { article_chain_root_id: number; qty: number; unit_cost_cents: number }[];
  }) => requestJson<RefillRow>("/refills", { method: "POST", json: payload }),

  // ---- Procurement (state-machine successor to refills) ----
  listProcurements: (
    params: {
      state?: ProcurementState;
      supplier?: string;
      from_?: string;
      to?: string;
      limit?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.state) q.set("state", params.state);
    if (params.supplier) q.set("supplier", params.supplier);
    if (params.from_) q.set("from_", params.from_);
    if (params.to) q.set("to", params.to);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return requestJson<ProcurementRow[]>(
      `/procurement${qs ? `?${qs}` : ""}`,
    );
  },
  getProcurement: (id: number) =>
    requestJson<ProcurementRow>(`/procurement/${id}`),
  createProcurement: (payload: CreateProcurementInput) =>
    requestJson<ProcurementRow>("/procurement", { method: "POST", json: payload }),
  /** Header edits send ``If-Match: <updated_at>`` so the server can
   * 412 if another operator raced us. Caller passes the row's known
   * ``updated_at`` ISO string. */
  patchProcurementHeader: (
    id: number,
    payload: UpdateProcurementHeaderInput,
    ifMatch: string,
  ) =>
    requestJson<ProcurementRow>(`/procurement/${id}`, {
      method: "PATCH",
      json: payload,
      headers: { "If-Match": ifMatch },
    }),
  patchProcurementState: (
    id: number,
    state: ProcurementState,
    ifMatch: string,
    options: { paid_in_cash?: boolean } = {},
  ) =>
    requestJson<ProcurementRow>(`/procurement/${id}/state`, {
      method: "PATCH",
      json: { state, ...(options.paid_in_cash ? { paid_in_cash: true } : {}) },
      headers: { "If-Match": ifMatch },
    }),
  addProcurementLine: (
    id: number,
    payload: ProcurementLineInput,
    ifMatch: string,
  ) =>
    requestJson<ProcurementLineRow>(`/procurement/${id}/lines`, {
      method: "POST",
      json: payload,
      headers: { "If-Match": ifMatch },
    }),
  patchProcurementLine: (
    id: number,
    lineId: number,
    payload: ProcurementLineInput,
    ifMatch: string,
  ) =>
    requestJson<ProcurementLineRow>(`/procurement/${id}/lines/${lineId}`, {
      method: "PATCH",
      json: payload,
      headers: { "If-Match": ifMatch },
    }),
  deleteProcurementLine: (
    id: number,
    lineId: number,
    ifMatch: string,
  ) =>
    requestJson<null>(`/procurement/${id}/lines/${lineId}`, {
      method: "DELETE",
      headers: { "If-Match": ifMatch },
    }),
  /** Hard-delete a draft procurement. Returns 400 for non-draft states
   * (received / cancelled must be cancelled through the state-machine
   * instead). If-Match enforced server-side. */
  deleteProcurement: (id: number, ifMatch: string) =>
    requestJson<null>(`/procurement/${id}`, {
      method: "DELETE",
      headers: { "If-Match": ifMatch },
    }),
  /** Returns the most-recent received procurement for the supplier (or
   * null if none). Powers the "Copy lines from last order" CTA on the
   * draft detail page. */
  lastForSupplier: (name: string) =>
    requestJson<ProcurementRow | null>(
      `/procurement/last-for-supplier?name=${encodeURIComponent(name)}`,
    ),
  /** Expenses attached to a procurement via ``linked_procurement_id``.
   * Soft-deleted rows are excluded; ordered newest-first. Powers the
   * "Expenses & Pfand returns" section on the procurement detail page. */
  listProcurementExpenses: (procurementId: number) =>
    requestJson<ExpenseRow[]>(`/procurement/${procurementId}/expenses`),
  /** Preflight floor + in-progress block (see
   * openspec/changes/add-procurement-backdate). Backs the create-modal
   * flow where lines aren't persisted yet; the caller hands over the
   * currently-lined-up chain root ids and gets the same floor / reason
   * the GET path would surface on a saved row. */
  receivedAtFloorPreflight: (article_ids: number[]) =>
    requestJson<ReceivedAtFloorResponse>("/procurement/received-at-floor", {
      method: "POST",
      json: { article_ids } satisfies ReceivedAtFloorRequest,
    }),
  /** Backdate impact preview (see
   * openspec/changes/add-procurement-backdate). Safe to call repeatedly
   * as the operator scrubs the modal's datetime picker — does NOT mutate
   * any data. Applies the same floor + in-progress checks as PATCH; if
   * the proposed value would be rejected, ``error_code`` is populated
   * inline so the modal can render the same reason without forcing a
   * doomed PATCH attempt. */
  backdateImpact: (procurementId: number, to: string) => {
    const qs = new URLSearchParams({ to });
    return requestJson<BackdateImpactResponse>(
      `/procurement/${procurementId}/backdate-impact?${qs.toString()}`,
    );
  },
  /** Distinct supplier autocomplete; canonical-key bucketed server-side
   * so casing / whitespace variants collapse to one suggestion. */
  suggestSuppliers: (q?: string, limit = 20) => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    qs.set("limit", String(limit));
    return requestJson<SupplierSuggestion[]>(`/suppliers/suggest?${qs.toString()}`);
  },

  // ---- Stocktakes ----
  listStocktakes: (params: { finalized?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.finalized !== undefined) q.set("finalized", String(params.finalized));
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return requestJson<StocktakeRow[]>(`/stocktakes${qs ? `?${qs}` : ""}`);
  },
  getStocktake: (id: number) => requestJson<StocktakeDetail>(`/stocktakes/${id}`),
  createStocktake: (payload: { scope?: "full" | "partial"; comment?: string } = {}) =>
    requestJson<StocktakeRow>("/stocktakes", {
      method: "POST",
      json: { scope: payload.scope ?? "full", comment: payload.comment },
    }),
  /** LEGACY upsert endpoint — Phase-2 stocktake UI uses PATCH on
   * ``/lines/{chain_root_id}`` instead. Kept for any existing callers. */
  upsertStocktakeLine: (
    stocktakeId: number,
    payload: { article_chain_root_id: number; counted_qty: number },
  ) =>
    requestJson<{ article_chain_root_id: number; counted_qty: number }>(
      `/stocktakes/${stocktakeId}/lines`,
      { method: "PUT", json: payload },
    ),
  /** Patch header fields on a stocktake (currently: ``counted_at``).
   * ``ifMatch`` is the stocktake's ``updated_at`` ISO; 412 on mismatch.
   * Post-finalize edits cascade ``effective_at`` onto every linked
   * ``stock_adjustment`` in the same transaction. Sending an empty body
   * is a no-op on the server. */
  patchStocktake: (
    stocktakeId: number,
    payload: { counted_at?: string | null; comment?: string | null },
    ifMatch: string,
  ) =>
    requestJson<StocktakeRow>(`/stocktakes/${stocktakeId}`, {
      method: "PATCH",
      json: payload,
      headers: { "If-Match": ifMatch },
    }),
  /** Phase-2 absolute-set on a single line. ``ifMatch`` is the
   * stocktake's ``updated_at`` ISO; 412 on mismatch. Pre-finalize this is
   * a straight upsert; post-finalize it cascades to the linked
   * ``stock_adjustment`` + auto-Expense rows. */
  patchStocktakeLine: (
    stocktakeId: number,
    chainRootId: number,
    counted_qty: number,
    ifMatch: string,
    /** Force ``line.counted_at = NOW`` on this PATCH. Used by the
     * "↻ re-walk" affordance — the operator has just physically
     * re-counted, and the per-line anchor should bind to this
     * second observation moment, not the original first-stamp. */
    restampCountedAt = false,
    /** 0051: optional structured term list. When omitted (legacy
     * clients + kiosk scanner), the backend drift trigger nulls the
     * stored expression and the next read synthesizes a sealed
     * legacy term. When supplied, the backend validates
     * ``sum(signed_terms) == counted_qty`` and persists both
     * columns atomically. */
    countExpression?: import("../utils/countExpression").Term[] | null,
    /** §5 (add-pos-recipes-and-cups): the measured contents of the OPEN
     * container at count time, in base units (mL for LITERS, g for GRAMS).
     * Seeds the open-container overlay's inter-count window (a hard cumulative
     * reset). Only sent when provided; the backend reads it conditionally. */
    measuredOpenBaseUnits?: number | null,
  ) =>
    requestJson<StocktakeLineRow>(
      `/stocktakes/${stocktakeId}/lines/${chainRootId}`,
      {
        method: "PATCH",
        json: {
          counted_qty,
          restamp_counted_at: restampCountedAt,
          ...(countExpression !== undefined
            ? { count_expression: countExpression }
            : {}),
          ...(measuredOpenBaseUnits !== undefined
            ? { measured_open_base_units: measuredOpenBaseUnits }
            : {}),
        },
        headers: { "If-Match": ifMatch },
      },
    ),
  /** Pre-finalize only. Backend 409s if the stocktake is already
   * finalized. */
  deleteStocktakeLine: (
    stocktakeId: number,
    chainRootId: number,
    ifMatch: string,
  ) =>
    requestJson<null>(
      `/stocktakes/${stocktakeId}/lines/${chainRootId}`,
      { method: "DELETE", headers: { "If-Match": ifMatch } },
    ),
  /** Operator-initiated re-walk: push ``line.counted_at`` to NOW.
   * Pre-finalize only — post-finalize, the cascade owns the anchor
   * and the operator must use the re-anchor flow instead. */
  restampStocktakeLineCountedAt: (
    stocktakeId: number,
    chainRootId: number,
    ifMatch: string,
  ) =>
    requestJson<StocktakeLineRow>(
      `/stocktakes/${stocktakeId}/lines/${chainRootId}/restamp-counted-at`,
      { method: "POST", headers: { "If-Match": ifMatch } },
    ),
  /** Idempotent on ``idempotency_key`` (uuid4 from the client). Returns
   * either ``{resolved:true, chain_root_id, counted_qty, qty_applied,
   * idempotent_replay}`` or ``{resolved:false, barcode}`` when the
   * barcode isn't known yet. */
  scanStocktake: (
    stocktakeId: number,
    payload: { barcode: string; qty: number; idempotency_key: string },
  ) =>
    requestJson<ScanResponse>(`/stocktakes/${stocktakeId}/scan`, {
      method: "POST",
      json: payload,
    }),
  /** Bootstrap snapshot: ``{chain_root_id → expected_qty_at_start}``
   * for every catalog article. The client uses this to compute mismatch
   * locally so live updates don't roundtrip per keystroke. */
  getStocktakeBootstrap: (stocktakeId: number) =>
    requestJson<StocktakeBootstrap>(`/stocktakes/${stocktakeId}/bootstrap`),
  previewFinalizeStocktake: (stocktakeId: number) =>
    requestJson<FinalizeProposal>(
      `/stocktakes/${stocktakeId}/preview-finalize`,
    ),
  finalizeStocktake: (id: number) =>
    requestJson<StocktakeRow>(`/stocktakes/${id}/finalize`, { method: "POST" }),
  /** Per-line diff of the frozen ``expected_qty_at_finalize`` against
   * what it would be if recomputed AS OF this stocktake's counted_at
   * using TODAY's procurement/sales/adjustments. Drives the re-anchor
   * preview modal. Only returns lines that are actually stale. */
  reanchorStocktakePreview: (stocktakeId: number) =>
    requestJson<ReanchorPreviewResponse>(
      `/stocktakes/${stocktakeId}/reanchor-preview`,
    ),
  /** Atomically refresh ``expected_qty_at_finalize`` + re-run the
   * cascade for every eligible line that isn't in ``skipChainRootIds``.
   * ``ifMatch`` is the stocktake's ``updated_at`` ISO. */
  reanchorStocktakeApply: (
    stocktakeId: number,
    skipChainRootIds: number[],
    ifMatch: string,
    previewSignature?: string,
  ) =>
    requestJson<ReanchorApplyResponse>(
      `/stocktakes/${stocktakeId}/reanchor`,
      {
        method: "POST",
        headers: { "If-Match": ifMatch },
        json: {
          skip_chain_root_ids: skipChainRootIds,
          preview_signature: previewSignature,
        },
      },
    ),
  /** Hard-cascade delete (removes stock_adjustment + auto-Expense rows).
   * ``ifMatch`` is the stocktake's ``updated_at`` ISO. */
  deleteStocktake: (id: number, ifMatch: string) =>
    requestJson<null>(`/stocktakes/${id}`, {
      method: "DELETE",
      headers: { "If-Match": ifMatch },
    }),

  // ---- Expenses (filtered list, used by the stocktake delete modal
  // §8.7 to count manually-edited linked rows AND by the §10
  // ExpensesPage workspace) ----
  listExpenses: (
    params: {
      from?: string;
      to?: string;
      category?: number;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    if (params.category) q.set("category", String(params.category));
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    const qs = q.toString();
    return requestJson<ExpenseRow[]>(`/expenses${qs ? `?${qs}` : ""}`);
  },
  /** Unified Expenses-page stream (real + drawer expenses + procurement +
   * sales + correction + settlement rows merged chronologically). Pairs
   * with ``TimelineRow`` / ``TimelineSums`` / ``TimelineResponse``. The
   * ``category`` filter narrows REAL rows only; virtual rows always show
   * within the date range. */
  getExpensesTimeline: (
    params: {
      from?: string;
      to?: string;
      category?: number;
      linked?: string;
      /** Source-kind subset filter. When non-empty, the backend drops
       * every row whose source is NOT in the list and recomputes ``sums``
       * over the filtered set. Encoded as repeated ``?source=`` query
       * params (FastAPI's ``list[str]`` default). */
      source?: string[];
      /** ``"daily"`` (default — collapses correction rows per-day and
       * settlement rows per-day-per-paymethod) or ``"granular"`` (one row
       * per underlying event). Omitted = backend default = daily. */
      aggregate?: "daily" | "granular";
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    if (params.category) q.set("category", String(params.category));
    if (params.linked) q.set("linked", params.linked);
    if (params.source && params.source.length > 0) {
      for (const s of params.source) q.append("source", s);
    }
    if (params.aggregate && params.aggregate !== "daily") {
      q.set("aggregate", params.aggregate);
    }
    const qs = q.toString();
    return requestJson<TimelineResponse>(
      `/expenses/timeline${qs ? `?${qs}` : ""}`,
    );
  },
  createExpense: (payload: {
    /** Optional: when omitted, the server defaults date to today. */
    date?: string;
    category_id: number;
    /** Non-Pfand cash portion. May be 0 on Pfand-only rows. */
    value_cents: number;
    comment?: string | null;
    /** Independent Pfand portion (refactor 2026-06-04 — not a subset of
     * value_cents). Must share the sign of value_cents when both are
     * non-zero; null means "no Pfand component". */
    pfand_value_cents?: number | null;
    receipt_path?: string | null;
    /** Optional FK to attach the expense to a procurement (delivery fee,
     * Pfand return, etc.). Drives the procurement detail page's
     * "Expenses & Pfand returns" section. */
    linked_procurement_id?: number | null;
    /** VAT rate id (FK into vat_rate). null / omitted = no rate (treated as
     * 0% for net math). Price stays gross. */
    vat_rate_id?: number | null;
  }) =>
    requestJson<ExpenseRow>("/expenses", {
      method: "POST",
      json: payload,
    }),
  /** PATCH /expenses/{id} — optional If-Match. */
  patchExpense: (
    id: number,
    payload: {
      date?: string;
      category_id?: number;
      /** Non-Pfand cash portion. May be 0 on Pfand-only rows. */
      value_cents?: number;
      comment?: string | null;
      /** Independent Pfand portion — see ExpenseRow JSDoc for the
       * sign-match rule. */
      pfand_value_cents?: number | null;
      receipt_path?: string | null;
      /** VAT rate id (FK into vat_rate). null = clear the rate. */
      vat_rate_id?: number | null;
    },
    ifMatch?: string,
  ) =>
    requestJson<ExpenseRow>(`/expenses/${id}`, {
      method: "PATCH",
      json: payload,
      ...(ifMatch ? { headers: { "If-Match": ifMatch } } : {}),
    }),
  deleteExpense: (id: number) =>
    requestJson<null>(`/expenses/${id}`, { method: "DELETE" }),

  // ---- Expense categories ----
  listExpenseCategories: (params: { include_archived?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.include_archived) q.set("include_archived", "true");
    const qs = q.toString();
    return requestJson<ExpenseCategoryRow[]>(
      `/expense-categories${qs ? `?${qs}` : ""}`,
    );
  },
  createExpenseCategory: (payload: { name: string; sort_order?: number }) =>
    requestJson<ExpenseCategoryRow>("/expense-categories", {
      method: "POST",
      json: payload,
    }),
  patchExpenseCategory: (
    id: number,
    payload: { name?: string; sort_order?: number; active?: boolean },
    ifMatch?: string,
  ) =>
    requestJson<ExpenseCategoryRow>(`/expense-categories/${id}`, {
      method: "PATCH",
      json: payload,
      ...(ifMatch ? { headers: { "If-Match": ifMatch } } : {}),
    }),
  deleteExpenseCategory: (id: number) =>
    requestJson<null>(`/expense-categories/${id}`, { method: "DELETE" }),
  reorderExpenseCategories: (orderedIds: number[]) =>
    requestJson<ExpenseCategoryRow[]>(`/expense-categories/reorder`, {
      method: "PUT",
      json: { ordered_ids: orderedIds },
    }),

  // ---- Pfand values (CRUD; §10.4) ----
  createPfandValue: (payload: {
    label: string;
    cents: number;
    sort_order?: number;
    kind?: "german" | "onsite";
  }) =>
    requestJson<PfandValueRow>("/pfand-values", {
      method: "POST",
      json: payload,
    }),
  patchPfandValue: (
    id: number,
    payload: {
      label?: string;
      cents?: number;
      sort_order?: number;
      active?: boolean;
    },
    ifMatch?: string,
  ) =>
    requestJson<PfandValueRow>(`/pfand-values/${id}`, {
      method: "PATCH",
      json: payload,
      ...(ifMatch ? { headers: { "If-Match": ifMatch } } : {}),
    }),
  deletePfandValue: (id: number) =>
    requestJson<null>(`/pfand-values/${id}`, { method: "DELETE" }),
  reorderPfandValues: (orderedIds: number[]) =>
    requestJson<PfandValueRow[]>(`/pfand-values/reorder`, {
      method: "PUT",
      json: { ordered_ids: orderedIds },
    }),

  // ---- VAT rates ----
  listVatRates: (includeArchived = false) =>
    requestJson<VatRateRow[]>(
      `/vat-rates${includeArchived ? "?include_archived=true" : ""}`,
    ),
  createVatRate: (payload: {
    name: string;
    percent: number;
    sort_order?: number;
  }) =>
    requestJson<VatRateRow>("/vat-rates", { method: "POST", json: payload }),
  patchVatRate: (
    id: number,
    payload: {
      name?: string;
      percent?: number;
      sort_order?: number;
      active?: boolean;
    },
    ifMatch?: string,
  ) =>
    requestJson<VatRateRow>(`/vat-rates/${id}`, {
      method: "PATCH",
      json: payload,
      ...(ifMatch ? { headers: { "If-Match": ifMatch } } : {}),
    }),
  deleteVatRate: (id: number) =>
    requestJson<null>(`/vat-rates/${id}`, { method: "DELETE" }),
  reorderVatRates: (orderedIds: number[]) =>
    requestJson<VatRateRow[]>(`/vat-rates/reorder`, {
      method: "PUT",
      json: { ordered_ids: orderedIds },
    }),

  // ---- Estimation exclusions ----
  listEstimationExclusions: () =>
    requestJson<EstimationExclusionRow[]>("/estimation/exclusions"),
  createEstimationExclusion: (payload: {
    from_at: string;
    to_at: string;
    label?: string | null;
    source?: "manual" | "festival";
  }) =>
    requestJson<EstimationExclusionRow>("/estimation/exclusions", {
      method: "POST",
      json: payload,
    }),
  patchEstimationExclusion: (
    id: number,
    payload: {
      from_at?: string;
      to_at?: string;
      label?: string | null;
      source?: "manual" | "festival";
    },
    ifMatch?: string,
  ) =>
    requestJson<EstimationExclusionRow>(`/estimation/exclusions/${id}`, {
      method: "PATCH",
      json: payload,
      ...(ifMatch ? { headers: { "If-Match": ifMatch } } : {}),
    }),
  deleteEstimationExclusion: (id: number) =>
    requestJson<null>(`/estimation/exclusions/${id}`, { method: "DELETE" }),

  // ---- Misc settings (operational window) ----
  getOperationalWindow: () =>
    requestJson<OperationalWindowRow>("/settings/operational-window"),
  patchOperationalWindow: (
    payload: {
      buildup_start_date?: string | null;
      takedown_end_date?: string | null;
    },
    ifMatch?: string,
  ) =>
    requestJson<OperationalWindowRow>("/settings/operational-window", {
      method: "PATCH",
      json: payload,
      ...(ifMatch ? { headers: { "If-Match": ifMatch } } : {}),
    }),

  // ---- Volunteer compensation rate (add-volunteer-compensation) ----
  getVolunteerRate: () =>
    requestJson<{ volunteer_comp_cents_per_day: number }>(
      "/settings/volunteer-rate",
    ),
  patchVolunteerRate: (volunteer_comp_cents_per_day: number) =>
    requestJson<{ volunteer_comp_cents_per_day: number }>(
      "/settings/volunteer-rate",
      { method: "PATCH", json: { volunteer_comp_cents_per_day } },
    ),

  // ---- User-scanner config ----
  getUserScannerConfig: () =>
    requestJson<UserScannerConfigRow>("/settings/user-scanner"),
  patchUserScannerConfig: (
    payload: {
      prefix?: string | null;
      numeric?: boolean | null;
      min_len?: number | null;
      max_len?: number | null;
    },
    ifMatch?: string,
  ) =>
    requestJson<UserScannerConfigRow>("/settings/user-scanner", {
      method: "PATCH",
      json: payload,
      ...(ifMatch ? { headers: { "If-Match": ifMatch } } : {}),
    }),

  // ---- Payment methods ----
  listPaymentMethods: (includeInactive = false) =>
    requestJson<PaymentMethodRow[]>(
      `/payment-methods${includeInactive ? "?include_inactive=true" : ""}`,
    ),
  createPaymentMethod: (payload: { name: string; sort_order?: number }) =>
    requestJson<PaymentMethodRow>("/payment-methods", {
      method: "POST",
      json: payload,
    }),
  updatePaymentMethod: (
    id: number,
    payload: { name?: string; sort_order?: number; active?: boolean },
  ) =>
    requestJson<PaymentMethodRow>(`/payment-methods/${id}`, {
      method: "PATCH",
      json: payload,
    }),
  deletePaymentMethod: (id: number) =>
    requestJson<null>(`/payment-methods/${id}`, { method: "DELETE" }),
  reorderPaymentMethods: (orderedIds: number[]) =>
    requestJson<PaymentMethodRow[]>(`/payment-methods/reorder`, {
      method: "PUT",
      json: { ordered_ids: orderedIds },
    }),

  // ---- Settlements ----
  listSettlements: (
    params: { user_id?: number; status_filter?: string; limit?: number } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.user_id !== undefined) q.set("user_id", String(params.user_id));
    if (params.status_filter) q.set("status_filter", params.status_filter);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return requestJson<SettlementRow[]>(`/settlements${qs ? `?${qs}` : ""}`);
  },
  getSettlement: (id: number) =>
    requestJson<SettlementRow>(`/settlements/${id}`),
  getSettlementInvoice: (id: number) =>
    requestJson<SettlementInvoice>(`/settlements/${id}/invoice`),
  createSettlement: (payload: {
    user_id: number;
    payment_method_id: number;
    comment?: string;
    disable_after?: boolean;
    /** Partial-settlement override. ``0 < amount_cents``; capped to
     * ``abs(balance)`` unless ``allow_topup`` is set. Omit / send null for the
     * full-balance default. Direction is always derived server-side from the
     * live balance sign. */
    amount_cents?: number | null;
    /** Let ``amount_cents`` exceed the debt — the surplus tops the user up into
     * credit (operator cash top-up). Only valid on a debtor. */
    allow_topup?: boolean;
  }) =>
    requestJson<SettlementRow>("/settlements", {
      method: "POST",
      json: payload,
    }),
  voidSettlement: (id: number) =>
    requestJson<SettlementRow>(`/settlements/${id}/void`, { method: "POST" }),
  reconcileSettlements: () =>
    requestJson<{ matched: number; expired: number; still_pending: number }>(
      "/settlements/reconcile",
      { method: "POST" },
    ),
  settlementReceiptUrl: (id: number) => `/api/settlements/${id}/receipt.html`,
  settlementCsvUrl: (params: {
    from_date?: string;
    to_date?: string;
    payment_method_id?: number;
    status_filter?: string;
    user_id?: number;
    /** Payment scopes: any of open_invoice / card / cash / other. Empty
     *  or all four = every settlement. */
    scopes?: string[];
    /** One row per user (settlement receipt_nos ;-joined, amounts summed)
     *  instead of one row per settlement. */
    group_by_user?: boolean;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.from_date) q.set("from_date", params.from_date);
    if (params.to_date) q.set("to_date", params.to_date);
    if (params.payment_method_id !== undefined)
      q.set("payment_method_id", String(params.payment_method_id));
    if (params.status_filter) q.set("status_filter", params.status_filter);
    if (params.user_id !== undefined) q.set("user_id", String(params.user_id));
    if (params.scopes && params.scopes.length)
      q.set("scopes", params.scopes.join(","));
    if (params.group_by_user) q.set("group_by_user", "true");
    const qs = q.toString();
    return `/api/settlements/export.csv${qs ? `?${qs}` : ""}`;
  },
  /** GET /api/settlements/export.zip — bulk-PDF export (one PDF per user,
   *  Rechnung for single-settlement users, Kontoübersicht for multi),
   *  packed in a ZIP. ``scopes`` decides which users are included (a user
   *  is in if they have ≥ 1 non-voided settlement in any selected scope):
   *  open_invoice / card / cash / other. Empty / all four = everyone. */
  settlementZipUrl: (scopes?: string[]) => {
    const qs = scopes && scopes.length ? `?scopes=${scopes.join(",")}` : "";
    return `/api/settlements/export.zip${qs}`;
  },

  // ---- Venue config ----
  getVenue: () => requestJson<VenueRow>("/venue"),
  updateVenue: (payload: {
    venue_name?: string | null;
    venue_address?: string | null;
    receipt_footer?: string | null;
  }) => requestJson<VenueRow>("/venue", { method: "PATCH", json: payload }),

  // ---- Backups ----
  getBackupConfig: () => requestJson<BackupConfigRow>("/backup-config"),
  updateBackupConfig: (payload: {
    enabled?: boolean;
    schedule_cron?: string;
    retention_hourly?: number;
    retention_daily?: number;
    retention_weekly?: number;
    target_path?: string;
  }) =>
    requestJson<BackupConfigRow>("/backup-config", {
      method: "PATCH",
      json: payload,
    }),
  listBackups: (includePurged = false) =>
    requestJson<BackupFileRow[]>(
      `/backups${includePurged ? "?include_purged=true" : ""}`,
    ),
  runBackupNow: () =>
    requestJson<{
      filename: string;
      size_bytes: number;
      sha256: string;
      purged_files: string[];
    }>("/backups/run-now", { method: "POST" }),
  restoreBackup: (filename: string) =>
    requestJson<{ status: string; filename: string }>(
      `/backups/${encodeURIComponent(filename)}/restore`,
      { method: "POST", json: { confirm: "RESTORE" } },
    ),
  backupDownloadUrl: (filename: string) =>
    `/api/backups/${encodeURIComponent(filename)}/download`,
  restoreStatus: () =>
    requestJson<{ in_progress: boolean; reason: string }>(
      "/backups/restore-status",
    ),

  // ---- Analytics ----
  analyticsOverview: (params: { from_date?: string; to_date?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from_date) q.set("from_date", params.from_date);
    if (params.to_date) q.set("to_date", params.to_date);
    const qs = q.toString();
    return requestJson<OverviewResponse>(
      `/analytics/overview${qs ? `?${qs}` : ""}`,
    );
  },
  analyticsSettlements: (params: { from_date?: string; to_date?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from_date) q.set("from_date", params.from_date);
    if (params.to_date) q.set("to_date", params.to_date);
    const qs = q.toString();
    return requestJson<SettlementsDashboardResponse>(
      `/analytics/settlements${qs ? `?${qs}` : ""}`,
    );
  },
  analyticsMismatchSummary: () =>
    requestJson<StocktakeMismatchRow[]>("/analytics/mismatch/summary"),
  analyticsMismatchLines: (params: { stocktake_id?: number; chain_root_id?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.stocktake_id !== undefined)
      q.set("stocktake_id", String(params.stocktake_id));
    if (params.chain_root_id !== undefined)
      q.set("chain_root_id", String(params.chain_root_id));
    const qs = q.toString();
    return requestJson<MismatchLine[]>(
      `/analytics/mismatch/lines${qs ? `?${qs}` : ""}`,
    );
  },
  mismatchCsvUrl: (params: { stocktake_id?: number; chain_root_id?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.stocktake_id !== undefined)
      q.set("stocktake_id", String(params.stocktake_id));
    if (params.chain_root_id !== undefined)
      q.set("chain_root_id", String(params.chain_root_id));
    const qs = q.toString();
    return `/api/analytics/mismatch.csv${qs ? `?${qs}` : ""}`;
  },
  analyticsStockDashboard: () =>
    requestJson<StockDashboardEntry[]>("/analytics/stock-dashboard"),

  // ---- Consumption analytics ----
  analyticsConsumption: (
    params: { dataset_id?: number; from_date?: string; to_date?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.dataset_id !== undefined)
      q.set("dataset_id", String(params.dataset_id));
    if (params.from_date) q.set("from_date", params.from_date);
    if (params.to_date) q.set("to_date", params.to_date);
    const qs = q.toString();
    return requestJson<ConsumptionResponse>(
      `/analytics/consumption${qs ? `?${qs}` : ""}`,
    );
  },
  consumptionCsvUrl: (
    section: "per_day" | "per_week" | "by_category" | "by_group" | "top_articles",
    params: { dataset_id?: number; from_date?: string; to_date?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.dataset_id !== undefined)
      q.set("dataset_id", String(params.dataset_id));
    if (params.from_date) q.set("from_date", params.from_date);
    if (params.to_date) q.set("to_date", params.to_date);
    const qs = q.toString();
    return `/api/analytics/consumption/${section}.csv${qs ? `?${qs}` : ""}`;
  },
  consumptionSources: () =>
    requestJson<ConsumptionSourceListItem[]>(
      "/analytics/consumption/sources",
    ),
  consumptionDatasets: () =>
    requestJson<ConsumptionDatasetSummary[]>(
      "/analytics/consumption/datasets",
    ),
  getConsumptionDataset: (id: number) =>
    requestJson<ConsumptionDatasetDetail>(
      `/analytics/consumption/datasets/${id}`,
    ),
  uploadConsumptionDataset: async (
    name: string,
    file: File,
    extra: {
      festival_from?: string;
      festival_to?: string;
      year?: number;
    } = {},
  ): Promise<ConsumptionDatasetDetail> => {
    const fd = new FormData();
    fd.append("name", name);
    fd.append("file", file, file.name);
    if (extra.festival_from) fd.append("festival_from", extra.festival_from);
    if (extra.festival_to) fd.append("festival_to", extra.festival_to);
    if (extra.year !== undefined) fd.append("year", String(extra.year));
    const r = await fetch("/api/analytics/consumption/datasets", {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!r.ok) {
      const text = await r.text();
      throw new ApiError(
        r.status,
        text || r.statusText,
        "POST",
        "/api/analytics/consumption/datasets",
      );
    }
    return (await r.json()) as ConsumptionDatasetDetail;
  },
  updateConsumptionDatasetMapping: (
    id: number,
    items: ConsumptionMappingUpdateItem[],
  ) =>
    requestJson<ConsumptionDatasetDetail>(
      `/analytics/consumption/datasets/${id}/mapping`,
      { method: "PUT", json: { items } },
    ),
  deleteConsumptionDataset: (id: number) =>
    requestJson<void>(`/analytics/consumption/datasets/${id}`, {
      method: "DELETE",
    }),

  // ---- Cash drawer ----
  /** Computed drawer ledger snapshot — expected balance, last count, and
   *  the most recent 50 cash flow events (settlements + drawer-affecting
   *  expenses). Zero new tables; entirely derived from existing data. */
  getCashDrawerState: () => requestJson<CashDrawerState>("/cash-drawer/state"),
  /** As-of expected drawer balance at an arbitrary instant. Same math
   *  as ``/state`` but every row whose event timestamp is after ``at``
   *  is excluded. ``at`` is ISO 8601 (offset optional; naive = UTC). */
  getCashDrawerSnapshot: (at: string) =>
    requestJson<CashDrawerSnapshot>(
      `/cash-drawer/snapshot?at=${encodeURIComponent(at)}`,
    ),
  /** Twelve month-end snapshots for ``year``. Future months are
   *  omitted; the current month clamps its ``at`` to now. */
  getCashDrawerMonthEnd: (year: number) =>
    requestJson<CashDrawerMonthEndRow[]>(
      `/cash-drawer/month-end?year=${year}`,
    ),
  /** Open the cash drawer — server resolves the ``Cash drawer opening``
   *  category and creates the opening expense row. Returns 409 when an
   *  active opening already exists unless ``force`` is true (which
   *  intentionally creates a duplicate; the multi-opening banner will
   *  flag it). */
  openCashDrawer: (payload: { value_cents: number; comment: string | null; force?: boolean }) =>
    requestJson<CashDrawerState>("/cash-drawer/open", {
      method: "POST",
      json: payload,
    }),
  /** Record a physical cash count. ``counted_cents`` is the SINGLE total
   *  the operator sees in the drawer — no separate Pfand input (Pfand
   *  isn't physically sortable from regular cash). A non-zero variance
   *  is materialised as a ``Cash variance`` expense row server-side. */
  recordCashDrawerCount: (payload: {
    counted_cents: number;
    comment?: string;
  }) =>
    requestJson<CashDrawerState>("/cash-drawer/count", {
      method: "POST",
      json: payload,
    }),
  /** Standalone "pay this procurement from the cash drawer" endpoint —
   *  used when the operator forgot to flag the procurement at receive
   *  time. Returns 409 if a Cash drawer expense is already linked. */
  payProcurementFromDrawer: (procurementId: number, comment?: string) =>
    requestJson<ExpenseRow>(
      `/procurement/${procurementId}/pay-from-drawer`,
      { method: "POST", json: { comment } },
    ),
  /** Guests with a non-zero balance that can be settled in cash from the
   *  cash-drawer "Barbezahlung" quick-settle widget. Server sorts by
   *  ABS(balance) DESC and caps at 200. Pass ``include_zero=true`` to
   *  include zero-balance guests (default false — they're not actionable). */
  listGuestSettleCandidates: (
    opts: { includeZero?: boolean; search?: string | null } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.includeZero) params.set("include_zero", "true");
    const trimmed = (opts.search ?? "").trim();
    if (trimmed) params.set("search", trimmed);
    const qs = params.toString();
    return requestJson<GuestSettleCandidate[]>(
      `/cash-drawer/guest-candidates${qs ? `?${qs}` : ""}`,
    );
  },

  // ---- UI preferences (universal per-user/shared widget config store) ----
  /** GET /api/ui-prefs/{key} — the stored JSON blob, or ``value: null`` when
   * unset (caller falls back to its default). Phase 1 is the shared row. */
  getUiPref: <T = unknown>(key: string) =>
    requestJson<{ key: string; value: T | null }>(
      `/ui-prefs/${encodeURIComponent(key)}`,
    ),
  /** PUT /api/ui-prefs/{key} — upsert the JSON blob for ``key``. */
  putUiPref: <T = unknown>(key: string, value: T) =>
    requestJson<{ key: string; value: T }>(
      `/ui-prefs/${encodeURIComponent(key)}`,
      { method: "PUT", json: { value } },
    ),

  // ---- Dashboard ----
  /** GET /api/dashboard/venue-summary — three scalars (strichliste sales,
   * procurement goods outflow, procurement Pfand outflow) backing the
   * Expenses page net-cash summary rows. Server-side cached ~30s; OK to
   * fire on every mount of the Expenses page. */
  getVenueSummary: () => requestJson<VenueSummary>("/dashboard/venue-summary"),
  /** GET /api/dashboard/pfand-balance — four Pfand cash-flow scalars
   * (received, returned, paid-out, net outstanding). Read-only Viewer+
   * surface. Used by the dashboard Pfand position tile. */
  getPfandBalance: () =>
    requestJson<PfandBalanceResponse>("/dashboard/pfand-balance"),
  /** GET /api/dashboard/outstanding-procurement — accounting.md §6.3
   * outstanding-invoices scalar plus a per-refill breakdown. Server-side
   * cached ~15s. Drives the dashboard "Outstanding invoices" tile. */
  getOutstandingProcurement: () =>
    requestJson<OutstandingProcurementResponse>(
      "/dashboard/outstanding-procurement",
    ),
  /** GET /api/dashboard/arrivals — per-day arrivals + departures roster
   * schedule. Server-side cached ~60s (roster-import data only changes
   * when a CSV is imported or arrival/departure dates are manually
   * edited). Drives the dashboard "Arrivals & Departures" widget. */
  getDashboardArrivals: () =>
    requestJson<ArrivalsResponse>("/dashboard/arrivals"),

  // ---- ERP ----
  getErpConfig: () => requestJson<ErpConfigRow>("/erp-config"),
  updateErpConfig: (payload: {
    enabled?: boolean;
    base_url?: string;
    auth_type?: string;
    auth_value?: string;
    sync_interval_minutes?: number;
  }) => requestJson<ErpConfigRow>("/erp-config", { method: "PATCH", json: payload }),
  syncErpNow: (force = false) =>
    requestJson<{
      rows_added: number;
      rows_updated: number;
      rows_removed: number;
      aborted_reason: string | null;
      error: string | null;
    }>("/erp-config/sync-now", { method: "POST", json: { force } }),

  // ---- SumUp settlement (Owner-only; add-sumup-settlement) ----
  /** GET /api/settings/sumup — config fields + every surface-policy row
   * (ordered by surface). Secrets are never echoed; only the
   * ``has_secret_key`` / ``has_affiliate_key`` presence booleans. */
  getSumupConfig: () => requestJson<SumupConfigOut>("/settings/sumup"),
  /** PUT /api/settings/sumup — updates config + upserts each supplied
   * surface row. Write-only secrets follow erp.py semantics: omit / send
   * ``undefined`` to keep the stored key, ``""`` to clear it, any other
   * string to encrypt + store. */
  updateSumupConfig: (payload: UpdateSumupConfigRequest) =>
    requestJson<SumupConfigOut>("/settings/sumup", {
      method: "PUT",
      json: payload,
    }),
  /** POST /api/settings/sumup/test — probes connectivity from the STORED
   * config (records ``last_test_at`` / ``last_test_status``). Returns
   * ``ok=false`` without calling SumUp when no key / merchant is set. */
  testSumup: () =>
    requestJson<SumupTestResult>("/settings/sumup/test", { method: "POST" }),
  /** GET /api/settings/sumup/readers — paired readers (503 when
   * unconfigured, 502 on a SumUp client error). */
  listSumupReaders: () =>
    requestJson<SumupReaderRow[]>("/settings/sumup/readers"),
  /** GET /api/settings/sumup/merchants — merchants the stored key can access
   * (live + sandbox), each with name + is_test_account, for the merchant
   * dropdown (filtered by the chosen environment). */
  listSumupMerchants: () =>
    requestJson<SumupMerchantOption[]>("/settings/sumup/merchants"),
  /** POST /api/settings/sumup/readers/pair — pair a reader by its pairing
   * code. Selecting it onto a surface happens via updateSumupConfig. */
  pairSumupReader: (payload: { code: string; name?: string | null }) =>
    requestJson<SumupReaderRow>("/settings/sumup/readers/pair", {
      method: "POST",
      json: payload,
    }),
  /** GET /api/settings/sumup/transactions — intents newest-first, each
   * with its per-leg statuses + the derived ``stuck`` flag. */
  listSumupTransactions: (
    params: { status?: string; surface?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.surface) q.set("surface", params.surface);
    const qs = q.toString();
    return requestJson<SumupTransactionRow[]>(
      `/settings/sumup/transactions${qs ? `?${qs}` : ""}`,
    );
  },
  /** GET /api/settings/sumup/transactions/stuck-count — paid-but-unsettled
   * intents (the recoverable window the poller retries). */
  sumupStuckCount: () =>
    requestJson<SumupStuckCountOut>("/settings/sumup/transactions/stuck-count"),
  /** DELETE /api/settings/sumup/transactions/{intentId} — force-close a
   * dead intent. 409 if it (or any leg) is paid: settle it instead. */
  deleteSumupTransaction: (intentId: number) =>
    requestJson<SumupTransactionRow>(
      `/settings/sumup/transactions/${intentId}`,
      { method: "DELETE" },
    ),
  /** POST /api/settlements/sumup-reader — operator card settlement on the
   * ``operator`` surface's selected reader (Office+). Primes a
   * ``sumup_payment`` + arms the reader leg and returns immediately; the
   * settlement is produced by the poller once the leg reaches ``paid``.
   * The caller then polls ``listSumupTransactions`` (or ``getSettlement``
   * once ``settlement_id`` appears) for the result. Returns 503
   * ``{error:"sumup_not_configured"}`` when SumUp is off / unconfigured. */
  operatorReaderSettle: (payload: {
    strichliste_user_id: number;
    amount_cents?: number | null;
    direction?: string | null;
  }) =>
    requestJson<SumupReaderSettleResult>("/settlements/sumup-reader", {
      method: "POST",
      json: payload,
    }),
  /** GET /api/settlements/sumup-status?intent_id=… (Office+) — poll one operator
   * intent (reconcile + status/legs), mirroring the kiosk's both-live status
   * poll so QR-scan and reader-tap are both detected. */
  operatorSumupStatus: (intentId: number) =>
    requestJson<SumupOperatorIntentView>(
      `/settlements/sumup-status?intent_id=${intentId}`,
    ),
  /** POST /api/settlements/sumup-cancel (Office+) — cancel an operator intent;
   * tears down its live QR/reader legs on SumUp (settles instead if a leg
   * already paid), freeing the user's one-active-intent slot. */
  operatorSumupCancel: (intentId: number) =>
    requestJson<SumupOperatorIntentView>("/settlements/sumup-cancel", {
      method: "POST",
      json: { intent_id: intentId },
    }),
  /** GET /api/settlements/sumup-availability (Office+) — whether the operator
   * reader settlement is usable, and why not. Drives the disabled note on the
   * "Card — SumUp reader" option. */
  sumupOperatorAvailability: () =>
    requestJson<{
      status: "ok" | "no_reader" | "disabled";
      /** Method ids the merchant accepts (card / apple_pay / google_pay / …);
       * empty unless status==="ok". paypal is dropped in the UI (reader-only). */
      payment_methods: string[];
      /** Operator surface's direction policy — drives whether a top-up (charge
       * past the debt) / charging a €0/credit user is allowed. */
      direction_policy: SumupDirectionPolicy;
      /** Per-surface top-up cap in cents; 0 unless direction_policy=both. The UI
       * derives max charge = abs(debt) + topup_max_cents. */
      topup_max_cents: number;
      /** Effective minimum charge (cents) — the surface min floored at SumUp's
       * €1.00. The reader amount input enforces this as its lower bound. */
      min_charge_cents: number;
    }>("/settlements/sumup-availability"),

  // ---- Estimation (legacy GET endpoint; removed when §15 cleanup ships) ----
  estimation: (
    params: {
      next_delivery_date?: string;
      subsequent_delivery_date?: string;
      safety_days?: number;
      trend_factor_override?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.next_delivery_date) q.set("next_delivery_date", params.next_delivery_date);
    if (params.subsequent_delivery_date)
      q.set("subsequent_delivery_date", params.subsequent_delivery_date);
    if (params.safety_days !== undefined)
      q.set("safety_days", String(params.safety_days));
    if (params.trend_factor_override !== undefined)
      q.set("trend_factor_override", String(params.trend_factor_override));
    const qs = q.toString();
    return requestJson<EstimationResponse>(`/estimation${qs ? `?${qs}` : ""}`);
  },
  estimationCsvUrl: (
    params: {
      next_delivery_date?: string;
      subsequent_delivery_date?: string;
      safety_days?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.next_delivery_date) q.set("next_delivery_date", params.next_delivery_date);
    if (params.subsequent_delivery_date)
      q.set("subsequent_delivery_date", params.subsequent_delivery_date);
    if (params.safety_days !== undefined)
      q.set("safety_days", String(params.safety_days));
    const qs = q.toString();
    return `/api/estimation.csv${qs ? `?${qs}` : ""}`;
  },

  // ---- New EB-Gamma-Poisson forecast (spec §11.6, §11.9) ----
  /** Run the new Empirical Bayes forecast. Returns one line per chain
   *  root with μ, the 95% CI, the operator-facing `order_qty`, and the
   *  per-line badges. The legacy `estimation()` GET above is gone in
   *  the §15 cleanup. */
  forecastEstimation: (body: ForecastRequest) =>
    requestJson<ForecastResponse>("/estimation/forecast", {
      method: "POST",
      json: body,
    }),
  /** Build a Procurement draft from the operator's ticked forecast
   *  rows. Server rounds qty up to pack_size if needed. The frontend
   *  navigates to `/stock/procurement/{procurement_id}` on success. */
  createEstimationDraft: (body: CreateDraftRequest) =>
    requestJson<CreateDraftResponse>("/estimation/create-draft", {
      method: "POST",
      json: body,
    }),

  // ---- Vouchers (add-voucher-system) ----
  createVoucherBatch: (payload: {
    prefix: string;
    amount_cents: number;
    expense_category_id: number;
    print_amount: boolean;
    auto_close?: boolean;
    allow_convert?: boolean;
    batch_comment?: string | null;
    pages: number;
  }) =>
    requestJson<{ creation_id: number; count: number }>("/vouchers/batch", {
      method: "POST",
      json: payload,
    }),
  setVoucherFlags: (payload: {
    ids?: number[];
    creation_id?: number;
    auto_close?: boolean;
    allow_convert?: boolean;
    batch_comment?: string | null;
  }) =>
    requestJson<{ updated: number }>("/vouchers/flags", {
      method: "POST",
      json: payload,
    }),
  listVouchers: (
    params: {
      creation_id?: number;
      category_id?: number;
      status?: string;
      redeemer_type?: string;
      hide_unused?: boolean;
      q?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.creation_id != null) qs.set("creation_id", String(params.creation_id));
    if (params.category_id != null) qs.set("category_id", String(params.category_id));
    if (params.status) qs.set("status", params.status);
    if (params.redeemer_type) qs.set("redeemer_type", params.redeemer_type);
    if (params.hide_unused) qs.set("hide_unused", "true");
    if (params.q) qs.set("q", params.q);
    const s = qs.toString();
    return requestJson<VoucherListResponse>(`/vouchers${s ? `?${s}` : ""}`);
  },
  voucherAccounting: () => requestJson<VoucherAccounting>("/vouchers/accounting"),
  voucherConfig: () =>
    requestJson<{ max_face_cents: number }>("/vouchers/config"),
  disableVouchers: (payload: { ids?: number[]; creation_id?: number }) =>
    requestJson<{ disabled: number }>("/vouchers/disable", {
      method: "POST",
      json: payload,
    }),
  sweepVouchers: (payload: { creation_id?: number } = {}) =>
    requestJson<{
      disabled_unused: number;
      swept_accounts: number;
      reclaimed_cents: number;
    }>("/vouchers/sweep", { method: "POST", json: payload }),
  listUnfundedVouchers: () =>
    requestJson<{ vouchers: UnfundedVoucherRow[] }>("/vouchers/unfunded"),
  refundVoucher: (voucherId: number) =>
    requestJson<{ ok: boolean }>(`/vouchers/${voucherId}/refund`, {
      method: "POST",
    }),
  getVoucherBatchPrint: (creationId: number) =>
    requestJson<VoucherBatchPrint>(`/vouchers/batch/${creationId}`),
  // Public voucher scan endpoints (shared with the kiosk; no-auth). Raw fetch so
  // the 409/404 redeem responses are read as DATA instead of thrown.
  voucherPreviewPublic: async (code: string): Promise<VoucherPublicResult> => {
    const r = await fetch("/api/public/voucher/preview", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    return (await r.json().catch(() => ({}))) as VoucherPublicResult;
  },
  voucherRedeemPublic: async (
    code: string,
    targetStrichlisteUserId?: number,
  ): Promise<VoucherPublicResult> => {
    const r = await fetch("/api/public/voucher/redeem", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        ...(targetStrichlisteUserId != null
          ? { target_strichliste_user_id: targetStrichlisteUserId }
          : {}),
      }),
    });
    return (await r.json().catch(() => ({}))) as VoucherPublicResult;
  },
  voucherConvertPublic: async (
    strichlisteUserId: number,
    realName: string,
    contact = "",
  ): Promise<{ ok?: boolean; error?: string }> => {
    const r = await fetch("/api/public/voucher/convert", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        strichliste_user_id: strichlisteUserId,
        real_name: realName,
        contact,
      }),
    });
    return (await r.json().catch(() => ({}))) as {
      ok?: boolean;
      error?: string;
    };
  },
  // user_types maps voucher-holder ids → "voucher"; voucher_flags carries the
  // per-account allow_convert / auto_close. Used to surface "Convert to Guest".
  getPublicGuests: () => requestJson<PublicGuestsResponse>("/public/guests"),

  // --- POS direct-sales (add-pos-direct-sales §2). Shell-agnostic: these back
  // both the admin POS screen and the standalone POS-display pages. ---
  posListStations: () => requestJson<PosStation[]>("/pos/stations"),
  posCreateOrder: (posId: number, clientIdempotencyKey?: string) =>
    requestJson<PosOrder>("/pos/orders", {
      method: "POST",
      json: { pos_id: posId, client_idempotency_key: clientIdempotencyKey },
    }),
  posGetOrder: (orderId: number) =>
    requestJson<PosOrder>(`/pos/orders/${orderId}`),
  posAddLine: (
    orderId: number,
    payload: {
      article_id: number;
      qty: number;
      unit_price_cents: number;
      kind?: "sale" | "return";
    },
  ) =>
    requestJson<PosOrder>(`/pos/orders/${orderId}/lines`, {
      method: "POST",
      json: payload,
    }),
  // Add an AD-HOC custom drink (add-pos-adhoc-custom-drink): one stockless sale
  // line carrying its own ingredient list, depleted at settle like a recipe.
  posAddCustomDrink: (
    orderId: number,
    payload: {
      name: string;
      unit_price_cents: number;
      ingredients: {
        consumable_chain_root_id: number;
        amount: number;
        unit_type: "liters" | "grams" | "count";
      }[];
    },
  ) =>
    requestJson<PosOrder>(`/pos/orders/${orderId}/custom-drink`, {
      method: "POST",
      json: payload,
    }),
  posUpdateLine: (orderId: number, lineId: number, qty: number) =>
    requestJson<PosOrder>(`/pos/orders/${orderId}/lines/${lineId}`, {
      method: "PATCH",
      json: { qty },
    }),
  posDeleteLine: (orderId: number, lineId: number) =>
    requestJson<PosOrder>(`/pos/orders/${orderId}/lines/${lineId}`, {
      method: "DELETE",
    }),
  posDiscardOrder: (orderId: number) =>
    requestJson<void>(`/pos/orders/${orderId}`, { method: "DELETE" }),
  posSettle: (
    orderId: number,
    payload: {
      tender: PosTender;
      tendered_cents?: number;
      // Voucher tender only: the scanned code that funds the order.
      voucher_code?: string;
    },
  ) =>
    requestJson<PosOrder>(`/pos/orders/${orderId}/settle`, {
      method: "POST",
      json: payload,
    }),
  // Signal the customer display that a tender popup is open (grouped "settling"
  // view) or closed (`null`). Transient, fire-and-forget; CARD self-signals via
  // its SumUp intent so it needs no present call.
  posPresent: (orderId: number, tender: PosTender | null) =>
    requestJson<{ presenting: boolean }>(`/pos/orders/${orderId}/present`, {
      method: "POST",
      json: { tender },
    }),
  // Read a scanned voucher's spendable value (face for fresh, live balance for a
  // redeemed card) — drives the sell screen's voucher-coverage display.
  posVoucherPreview: (code: string) =>
    requestJson<PosVoucherPreview>("/pos/voucher-preview", {
      method: "POST",
      json: { code },
    }),
  posCreateStation: (payload: {
    name: string;
    default_opening_float_cents?: number | null;
    staff_comp_category_id?: number | null;
    client_comp_category_id?: number | null;
  }) =>
    requestJson<PosStation>("/pos/stations", { method: "POST", json: payload }),
  // One-call catalog bundle (products + names + barcodes + POS price + onsite
  // pfand): backs scan-to-add + display line names.
  posCatalogSync: () => requestJson<PosCatalogSync>("/pos/catalog-sync"),

  // --- Cups & on-site deposits (add-pos-recipes-and-cups §1). Four one-tap
  // quick-actions on a draft order. CUP moves inventory (the configured cup
  // article — its StockAdjustment IS the cup stock); DEPOSIT moves the loose
  // on-site liability. `out` = leaves the bar = a charge; `in` = comes back =
  // a refund. One cup, one deposit → no picker. ---
  posCupAction: (orderId: number, direction: "out" | "in", qty = 1) =>
    requestJson<PosOrder>(`/pos/orders/${orderId}/cup`, {
      method: "POST",
      json: { direction, qty },
    }),
  posDepositAction: (orderId: number, direction: "out" | "in", qty = 1) =>
    requestJson<PosOrder>(`/pos/orders/${orderId}/deposit`, {
      method: "POST",
      json: { direction, qty },
    }),
  // Venue cup/deposit config (the one cup article + its deposit + the on-site
  // pfand). GET is POS-capability; PUT is Owner-only + validated fail-loud.
  posGetCupConfig: () => requestJson<PosCupConfig>("/pos/cup-config"),
  // card_background_enabled is READ-ONLY here (set on the SumUp settings page);
  // the cups page sends only the cup/deposit fields.
  posPutCupConfig: (payload: Omit<PosCupConfig, "card_background_enabled">) =>
    requestJson<PosCupConfig>("/pos/cup-config", { method: "PUT", json: payload }),

  // --- Card (DUAL-RAIL: the backend arms a reader checkout AND a hosted
  // checkout (scan-to-pay QR) for the same order/amount, first-paid-wins,
  // deactivates the loser, auto-refunds a double-capture. `online_checkout_url`
  // is the QR; `reader_armed` says the Solo is also live. ---
  posPayCard: (orderId: number) =>
    requestJson<CardPrimeResponse>(`/pos/orders/${orderId}/pay-card`, {
      method: "POST",
    }),
  posCardStatus: (orderId: number) =>
    requestJson<CardStatusResponse>(`/pos/orders/${orderId}/card-status`),
  posCancelCard: (orderId: number) =>
    requestJson<CardStatusResponse>(`/pos/orders/${orderId}/cancel-card`, {
      method: "POST",
    }),
  // Re-present the amount to the physical reader (a fresh reader checkout on the
  // SAME live intent) after SumUp's ~60s "time to start" device window lapsed — the
  // QR rail is untouched. Money-safe server-side (a paid tap settles, not re-armed).
  posRearmReader: (orderId: number) =>
    requestJson<CardStatusResponse>(`/pos/orders/${orderId}/rearm-reader`, {
      method: "POST",
    }),

  // --- Corrections (void / line-exact refund of a SETTLED order) ---
  posCorrectOrder: (
    orderId: number,
    payload: { client_idempotency_key: string; line_ids?: number[] | null },
  ) =>
    requestJson<CorrectionResponse>(`/pos/orders/${orderId}/corrections`, {
      method: "POST",
      json: payload,
    }),

  // --- Cash-drawer sessions ---
  posCurrentSession: (posId: number) =>
    requestJson<PosSession | null>(`/pos/sessions/current?pos_id=${posId}`),
  posOpenSession: (payload: {
    pos_id: number;
    opening_float_cents: number;
    denominations?: PosDenomination[];
  }) => requestJson<PosSession>("/pos/sessions", { method: "POST", json: payload }),
  posAddCashMovement: (
    sessionId: number,
    payload: {
      kind: "drop" | "pay_in" | "payout";
      amount_cents: number;
      reason?: string | null;
    },
  ) =>
    requestJson<PosSession>(`/pos/sessions/${sessionId}/movements`, {
      method: "POST",
      json: payload,
    }),
  posMidCount: (
    sessionId: number,
    payload: {
      counted_cents: number;
      mode?: "informational" | "rebaseline";
      denominations?: PosDenomination[];
    },
  ) =>
    requestJson<PosSession>(`/pos/sessions/${sessionId}/counts`, {
      method: "POST",
      json: payload,
    }),
  posCloseSession: (
    sessionId: number,
    payload: { counted_cents: number; denominations?: PosDenomination[] },
  ) =>
    requestJson<PosSessionClose>(`/pos/sessions/${sessionId}/close`, {
      method: "POST",
      json: payload,
    }),

  // --- Customer-facing display (pos-customer-display) ---
  // Operator-issued, station-scoped read-only token + the standalone path the
  // second screen opens. The display itself reads `posDisplayState(token)`
  // unauthenticated.
  posDisplayToken: (stationId: number) =>
    requestJson<DisplayTokenResponse>(
      `/pos/stations/${stationId}/display-token`,
    ),
  posDisplayState: (token: string) =>
    requestJson<PosDisplayState>(
      `/pos/display/state?token=${encodeURIComponent(token)}`,
    ),

  // --- Recipes (add-pos-recipes-and-cups §3). A recipe = a sellable article
  // (its chain root) + ingredient rows (consumable root, amount, unit). Selling
  // a recipe fans out the ingredient depletion at settle (the recipe holds no
  // stock of its own). Reads surface the DERIVED availability (limiting
  // ingredient) + the PROJECTED per-serving COGS (display-only — no Expense,
  // ingredients are already cost-booked at procurement). Writes are Owner-gated
  // + validated server-side (dimension match, portion-sellable, no nesting). ---
  listRecipes: () => requestJson<RecipeRow[]>("/recipes"),
  createRecipe: (payload: CreateRecipeRequest) =>
    requestJson<RecipeRow>("/recipes", { method: "POST", json: payload }),
  replaceRecipeIngredients: (
    recipeId: number,
    ingredients: RecipeIngredientIn[],
  ) =>
    requestJson<RecipeRow>(`/recipes/${recipeId}/ingredients`, {
      method: "PUT",
      json: ingredients,
    }),
  deleteRecipe: (recipeId: number) =>
    requestJson<null>(`/recipes/${recipeId}`, { method: "DELETE" }),
};

// --- POS direct-sales wire types (mirror app/routes/pos.py) ----------------

export type PosTender =
  | "cash"
  | "card"
  | "staff_comp"
  | "client_comp"
  | "voucher";

/** Scanned-voucher preview for the sell screen (add-pos-voucher-payment).
 * `available_cents` = what the voucher can put toward the order RIGHT NOW: the
 * face for a fresh (unredeemed) voucher, the holder's live balance for an
 * already-redeemed card; null when unknown. */
export interface PosVoucherPreview {
  status:
    | "unredeemed"
    | "redeemed"
    | "already_redeemed"
    | "disabled"
    | "not_found";
  available_cents: number | null;
  redeemed_user_id: number | null;
}

export interface PosStation {
  id: number;
  name: string;
  default_opening_float_cents: number | null;
  staff_comp_category_id: number | null;
  client_comp_category_id: number | null;
  active: boolean;
}

export interface PosOrderLine {
  id: number;
  kind: "sale" | "return" | "comp" | "deposit";
  article_id: number | null;
  article_chain_root_id: number | null;
  pfand_value_id: number | null;
  parent_line_id: number | null;
  unit_price_cents: number;
  qty: number;
  /** Ad-hoc custom-drink name (NULL-article line); null for normal/deposit lines. */
  custom_name: string | null;
}

export interface PosOrder {
  id: number;
  pos_id: number;
  status: "draft" | "settled" | "voided" | "payment_failed";
  tender: PosTender | null;
  total_cents: number;
  tendered_cents: number | null;
  change_due_cents: number | null;
  receipt_no: string | null;
  captured_at: string;
  lines: PosOrderLine[];
}

/** Venue cup + on-site deposit config (the misc_settings singleton). All
 * nullable = unconfigured. `cup_article_id` is a strichliste article id (its
 * StockAdjustment IS the cup inventory); `deposit_pfand_value_id` is an active
 * on-site pfand value. */
export interface PosCupConfig {
  cup_article_id: number | null;
  cup_price_cents: number | null;
  deposit_pfand_value_id: number | null;
  /** Global bottle-pfand kill-switch. Off → no deposit is auto-added at the till
   * (even per-article) and the Bottle-in button is hidden. */
  deposit_enabled: boolean;
  /** POS card flow (from sumup_config). true → background the card (park + free
   * the till + poll a pill); false → a foreground/blocking card payment. */
  card_background_enabled: boolean;
}

// --- Recipes (mirror app/routes/recipes.py) --------------------------------

/** One recipe ingredient: a consumable article (its chain root) drawn down by
 * `amount` of `unit_type` per serving. `liters` → mL base (amount is mL),
 * `grams` → g base (amount is g), `count` → whole units. */
export interface RecipeIngredientIn {
  consumable_chain_root_id: number;
  amount: number;
  unit_type: "liters" | "grams" | "count";
}

export interface RecipeIngredientRow extends RecipeIngredientIn {
  id: number;
}

export interface CreateRecipeRequest {
  article_chain_root_id: number;
  ingredients: RecipeIngredientIn[];
}

/** A recipe + its derived readouts. `available` = servings buildable from the
 * limiting ingredient's current whole-units + open-container overlay (null when
 * an ingredient lacks metadata); `cogs_cents` = projected ingredient cost per
 * serving (display projection only — no Expense is ever booked for it). */
export interface RecipeRow {
  id: number;
  article_chain_root_id: number;
  ingredients: RecipeIngredientRow[];
  available: number | null;
  cogs_cents: number | null;
}

// --- Card (dual-rail reader + scan-to-pay QR) ------------------------------

/** Prime result for a card tender. `online_checkout_url` is the hosted-checkout
 * URL to render as a scan-to-pay QR; `reader_armed` says the Solo reader is also
 * live. Both rails settle the SAME order/amount, first-paid-wins. */
export interface CardPrimeResponse {
  order_id: number;
  order_status: string;
  intent_id: number;
  intent_status: string;
  amount_cents: number;
  offered_channels: string[];
  online_checkout_url: string | null;
  reader_armed: boolean;
}

export interface CardStatusResponse {
  order_id: number;
  order_status: string;
  receipt_no: string | null;
  intent_id: number | null;
  intent_status: string | null;
  // Paid on the card but the order isn't SETTLED yet — keep polling until false.
  settlement_pending: boolean;
  // `reader_armed` = the Solo currently holds a live reader checkout. `reader_failed`
  // = the tender is still awaiting but the reader leg went terminal-unpaid (recorded
  // decline / our 90s sweep) while the QR rail keeps it alive — surface a re-present
  // prompt. (SumUp's ~60s device timeout is undetectable server-side; the hook adds
  // a time-based fallback on top of this.)
  reader_armed: boolean;
  reader_failed: boolean;
}

// --- Corrections -----------------------------------------------------------

export interface CorrectionResponse {
  original_order_id: number;
  original_receipt_no: string | null;
  correction_receipt_no: string;
  correction_type: "void" | "partial_refund";
  tender: string;
  reversed_line_ids: number[];
  refunded_total_cents: number;
  card_refund: "issued" | "failed" | "sumup_disabled" | "no_transaction" | null;
}

// --- Catalog bundle --------------------------------------------------------

export interface PosCatalogProduct {
  article_id: number;
  chain_root_id: number;
  name: string;
  pos_price_cents: number;
  category_id: number | null;
  category_name: string | null;
  onsite_pfand_value_id: number | null;
  barcodes: string[];
  active: boolean;
  vat_rate_id: number | null;
}
export interface PosCatalogCategory {
  id: number;
  name: string;
  sort_order: number;
  active: boolean;
}
export interface PosCatalogPfand {
  id: number;
  label: string;
  cents: number;
  active: boolean;
}
export interface PosCatalogStation {
  id: number;
  name: string;
  default_opening_float_cents: number | null;
  active: boolean;
}
export interface PosCatalogSync {
  synced_at: string;
  stations: PosCatalogStation[];
  categories: PosCatalogCategory[];
  onsite_pfand: PosCatalogPfand[];
  products: PosCatalogProduct[];
}

// --- Cash-drawer sessions --------------------------------------------------

export interface PosDenomination {
  denomination_cents: number;
  quantity: number;
}
export interface PosSession {
  id: number;
  pos_id: number;
  status: string;
  opening_float_cents: number;
  opened_at: string;
  closed_at: string | null;
  expected_cents: number;
  counted_cents: number | null;
  variance_cents: number | null;
  comp_count: number;
  comp_total_cents: number;
}
export interface PosSessionClose extends PosSession {
  deposit_cash_in_cents: number;
  deposit_cash_out_cents: number;
  sales_variance_cents: number;
  drawer_negative: boolean;
}

// --- Customer-facing display ----------------------------------------------

export interface DisplayTokenResponse {
  station_id: number;
  station_name: string;
  token: string;
  /** Relative SPA path the second screen opens (a plain browser, no login). */
  path: string;
}
export interface PosDisplayLine {
  name: string;
  kind: "sale" | "return" | "deposit" | "comp";
  qty: number;
  unit_price_cents: number;
  line_total_cents: number;
  /** Set when this line is bundled to a drink (auto-added cup / deposit). */
  parent_line_id: number | null;
}
export interface PosDisplayCard {
  status: "waiting" | "paid" | "declined" | "expired";
  /** Hosted-checkout URL → render as a scan-to-pay QR. */
  qr_url: string | null;
  reader_armed: boolean;
}
export interface PosDisplayState {
  station_id: number;
  station_name: string;
  state: "idle" | "building" | "settling" | "card" | "settled";
  lines: PosDisplayLine[];
  total_cents: number;
  card: PosDisplayCard | null;
  receipt_no: string | null;
  settled_total_cents: number | null;
  change_due_cents: number | null;
  server_time: string;
}

export interface PublicGuestsResponse {
  strichliste_user_ids: number[];
  user_types: Record<string, string>;
  voucher_flags: Record<string, { auto_close: boolean; allow_convert: boolean }>;
}

export interface VoucherPublicResult {
  status:
    | "unredeemed"
    | "redeemed"
    | "already_redeemed"
    | "disabled"
    | "not_found"
    | "error";
  amount_cents?: number;
  redeemed_user_id?: number | null;
  error?: string;
}

export interface VoucherRow {
  id: number;
  code: string;
  prefix: string;
  amount_cents: number;
  print_amount: boolean;
  creation_id: number;
  created_at: string | null;
  status: string; // "unused" | "redeemed" | "disabled"
  disabled: boolean;
  redeemed_at: string | null;
  redeemed_by_strichliste_user_id: number | null;
  redeemer_name: string | null;
  redeemer_type: string | null;
  redeemer_voucher_count: number;
  spent_cents: number | null;
  swept: boolean;
  auto_close: boolean;
  allow_convert: boolean;
  batch_comment: string | null;
}

export interface VoucherSummary {
  redeemed: number;
  unused: number;
  disabled: number;
  face_out_cents: number;
  spent_cents: number;
  unspent_cents: number;
}

export interface VoucherListResponse {
  vouchers: VoucherRow[];
  summary: VoucherSummary;
  // Same roll-up per batch, keyed by creation_id (as a string). Lets each
  // batch row show identical figures to the grand total.
  batch_summaries: Record<string, VoucherSummary>;
}

export interface VoucherAccounting {
  redeemed_per_day: { date: string; amount_cents: number }[];
  redeemed_total_cents: number; // net of sweeps (§19.2)
  redeemed_gross_cents: number;
  swept_total_cents: number;
  unspent_cents: number;
  consumed_cents: number;
  voucher_account_out_cents: number;
}

export interface UnfundedVoucherRow {
  id: number;
  code: string;
  amount_cents: number;
  creation_id: number;
  redeemed_by_strichliste_user_id: number | null;
  redeemed_at: string | null;
}

export interface VoucherBatchPrint {
  creation_id: number;
  prefix: string;
  amount_cents: number;
  print_amount: boolean;
  category_name: string | null;
  created_at: string | null;
  created_by_label: string;
  count: number;
  vouchers: { code: string; amount_cents: number }[];
}

export interface EstimationMember {
  chain_root_id: number;
  unit_size: number;
  suggested_qty: number;
}

export interface EstimationGroup {
  group_id: number | null;
  group_name: string;
  category_id: number | null;
  category_name: string | null;
  anchor_kind: string;
  anchor_at: string;
  days_since_anchor: number;
  consumption_per_day: number;
  trend_factor: number;
  current_stock_units: number;
  total_demand_units: number;
  group_shortfall_units: number;
  horizon_days: number;
  warnings: string[];
  members: EstimationMember[];
}

export interface EstimationResponse {
  now: string;
  next_delivery_date: string;
  subsequent_delivery_date: string;
  safety_days: number;
  trend_factor: number;
  trend_factor_source: "computed" | "override";
  groups: EstimationGroup[];
}

// ---- New EB Gamma-Poisson forecast (spec §11.6 / §11.9) ----
// Mirrors ``app.routes.estimation.ForecastRequest/ForecastResponse``.
// Keep field names + nullability in lockstep with the backend Pydantic
// models — JSON-positional, so any drift surfaces as undefined fields
// at runtime.

export interface ForecastExclusionRange {
  /** ISO 8601 timestamp (inclusive). */
  from_at: string;
  /** ISO 8601 timestamp (exclusive). */
  to_at: string;
}

export interface ForecastRequest {
  /** ISO 8601 timestamp — must be in the future. */
  must_last_until: string;
  /** Signed integer: positive = arrivals, negative = departures. */
  people_change?: number;
  /** Percentage 0..50 in the UI; backend tolerates -100..+500. */
  safety_margin_pct?: number;
  /** Optional explicit basis range; defaults to last 30 days server-side. */
  basis_from?: string | null;
  basis_to?: string | null;
  include_pending_orders?: boolean;
  /** Resolved snapshot of /api/estimation/exclusions the forecast should use. */
  exclusion_ranges?: ForecastExclusionRange[];
}

export interface CohortContributorOut {
  chain_root_id: number;
  article_name: string;
  /** Sum of transaction quantity over the basis window for this chain. */
  basis_qty: number;
  /** Distinct strichliste user_ids that bought from this chain in basis. */
  distinct_buyers: number;
  /** True when the chain's latest article is strichliste-inactive. */
  is_deleted: boolean;
}

export interface ForecastLineOut {
  chain_root_id: number;
  article_name: string;
  /** Predictive mean over the target window. */
  mu_forecast: number;
  /** 95% predictive CI low bound (clipped at 0). */
  ci_low: number;
  /** 95% predictive CI high bound. */
  ci_high: number;
  /** μ_forecast with the safety margin applied (pre-pack-rounding). */
  mu_target: number;
  /** Net current stock − pending arrivals already reflected by the engine. */
  stock_available: number;
  /** In-horizon pending procurement quantity (already-ordered but unreceived). */
  pending_arrivals: number;
  /** Server-suggested default qty (max(0, μ_target − stock − pending), rounded up to pack). */
  order_qty: number;
  pack_size: number | null;
  /** Brand-new article — group-rate fallback used. UI shows a blue 🆕 badge. */
  is_new_article: boolean;
  /** Sparse-history article — wider CI. UI shows a yellow ⚠ badge. */
  is_low_confidence: boolean;
  /** "article" | "category" | "flat" — for transparency. */
  hour_of_day_source: string;
  /** "cohort" | "category" | "group" | "unavailable". */
  prior_source: string;
  /** True when the chain's latest article is strichliste-inactive.
   *  UI hides the row when order_qty == 0 and renders a "deleted"
   *  badge after the article name when order_qty > 0 (anomaly). */
  is_deleted: boolean;
  /** Members of the article's category cohort (incl. self). Populated
   *  whenever category_id is set on ArticleMetadata; empty otherwise.
   *  Drives the tooltip on the article-name cell. */
  category_cohort: CohortContributorOut[];
  /** Members of the article's product-group cohort (incl. self). */
  group_cohort: CohortContributorOut[];
}

export interface ForecastResponse {
  per_article: ForecastLineOut[];
  /** Sum of order_qty across every line (server-computed; UI re-sums after edits). */
  total_order_qty: number;
  /** Sum of order_qty × unit cost across every line. */
  total_order_value_cents: number;
  basis_from: string;
  basis_to: string;
  target_start: string;
  target_end: string;
  /** Forecast-level warnings the engine surfaces verbatim:
   *  ``festival_period_not_excluded`` — basis overlaps a configured
   *  festival window without an exclusion range covering it. */
  warnings: string[];
}

export interface CreateDraftLine {
  chain_root_id: number;
  qty: number;
}

export interface CreateDraftRequest {
  lines: CreateDraftLine[];
  /** Optional delivery_date (YYYY-MM-DD); the procurement detail page can fill later. */
  delivery_date?: string | null;
  /** Optional supplier name; up to 128 chars. */
  supplier?: string | null;
}

export interface CreateDraftResponse {
  procurement_id: number;
}

export interface PaymentMethodRow {
  id: number;
  name: string;
  code: string;
  active: boolean;
  sort_order: number;
  system_seeded: boolean;
}

export interface SettlementInvoice {
  user_name: string;
  user_email: string | null;
  settlements: {
    receipt_no: string;
    settled_at: string | null;
    amount_cents: number;
    payment_method: string;
    due: boolean;
  }[];
  amount_paid_cents: number;
  amount_due_cents: number;
  open_balance_cents: number;
  credits_cents: number;
  vat_groups: {
    percent: number;
    net_cents: number;
    vat_cents: number;
    gross_cents: number;
  }[];
  consumption_net_cents: number;
  consumption_vat_cents: number;
  consumption_gross_cents: number;
}

export interface SettlementRow {
  id: number;
  receipt_no: string;
  settled_at: string;
  user_identity_id: number | null;
  strichliste_user_id: number;
  user_nickname: string | null;
  payment_method_id: number;
  payment_method_name: string | null;
  amount_cents: number;
  direction: "user_to_bank" | "bank_to_user" | "none";
  status:
    | "pending"
    | "completed"
    | "failed"
    | "pending_blocked"
    | "voided";
  comment: string | null;
  disable_after: boolean;
  strichliste_transaction_id: number | null;
  last_error: string | null;
  operator_id: number | null;
  operator_label: string | null;
  created_at: string;
  updated_at: string;
}

export interface VenueRow {
  venue_name: string | null;
  venue_address: string | null;
  logo_path: string | null;
  receipt_footer: string | null;
}

export interface BackupConfigRow {
  enabled: boolean;
  schedule_cron: string;
  retention_hourly: number;
  retention_daily: number;
  retention_weekly: number;
  target_path: string;
  last_run: string | null;
  last_status: string | null;
}

export interface BackupFileRow {
  id: number;
  filename: string;
  size_bytes: number;
  sha256: string;
  created_at: string;
  purged: boolean;
  download_url: string | null;
}

export interface BankHealth {
  bank_balance_cents: number;
  upper_boundary_cents: number | null;
  utilization_pct: number | null;
  status: "green" | "amber" | "red" | "unknown";
}

export interface TopArticleRow {
  article_id: number;
  name: string;
  qty_sold: number;
  revenue_cents: number;
}

export interface TopCategoryRow {
  category_id: number | null;
  category_name: string;
  revenue_cents: number;
}

export interface TopUserRow {
  user_identity_id: number | null;
  strichliste_user_id: number;
  nickname: string;
  email: string | null;
  spend_cents: number;
}

export interface OverviewResponse {
  scope_from: string | null;
  scope_to: string;
  active_users: number;
  total_user_balance_cents: number;
  bank_balance_cents: number;
  transactions_in_scope: number;
  bank_health: BankHealth;
  top_articles: TopArticleRow[];
  top_categories: TopCategoryRow[];
  top_users: TopUserRow[];
}

export interface SettlementsByMethodRow {
  payment_method_id: number;
  payment_method_name: string;
  count: number;
  total_cents: number;
}

export interface SettlementsTimePoint {
  date: string;
  total_cents: number;
  count: number;
}

export interface SettlementsDashboardResponse {
  scope_from: string;
  scope_to: string;
  total_completed_cents: number;
  total_completed_count: number;
  by_method: SettlementsByMethodRow[];
  time_series: SettlementsTimePoint[];
  pending_or_failed_count: number;
}

export interface StocktakeMismatchRow {
  stocktake_id: number;
  finalized_at: string | null;
  scope: "full" | "partial";
  line_count: number;
  total_mismatch: number;
}

export interface MismatchLine {
  stocktake_id: number;
  chain_root_id: number;
  name: string | null;
  expected_qty: number;
  counted_qty: number;
  mismatch_qty: number;
}

export interface ConsumptionPhase {
  name: string;
  from_at: string;
  to_at: string;
}

export interface ConsumptionBucket {
  taps: number;
  units: number;
  revenue_cents: number;
}

export interface ConsumptionDayRow extends ConsumptionBucket {
  date: string;
  phase: string;
}

export interface ConsumptionWeekRow extends ConsumptionBucket {
  iso_week: string;
}

export interface ConsumptionPhaseRow extends ConsumptionBucket {
  phase: string;
}

export interface ConsumptionCategoryRow extends ConsumptionBucket {
  phase: string;
  category_id: number | null;
  category: string;
}

export interface ConsumptionGroupRow extends ConsumptionBucket {
  phase: string;
  category_id: number | null;
  category: string;
  product_group_id: number | null;
  product_group: string | null;
}

export interface ConsumptionTopArticleRow extends ConsumptionBucket {
  article_id: number | null;
  chain_root_id: number | null;
  name: string;
  category: string | null;
  product_group: string | null;
}

export interface ConsumptionDailyOpenerCloser {
  date: string;
  ts: string;
  article_name: string;
  quantity: number;
  revenue_cents: number;
}

export interface ConsumptionPeakHourRow {
  date: string;
  hour: number;
  taps: number;
}

export interface ConsumptionArticleLifecycle {
  name: string;
  first_seen: string;
  last_seen: string;
  taps: number;
  units: number;
}

export interface ConsumptionOutliers {
  daily_openers: ConsumptionDailyOpenerCloser[];
  daily_closers: ConsumptionDailyOpenerCloser[];
  peak_hours: ConsumptionPeakHourRow[];
  vanished_articles: ConsumptionArticleLifecycle[];
  new_articles: ConsumptionArticleLifecycle[];
}

export interface ConsumptionResponse {
  source: string;
  source_label: string;
  scope_from: string;
  scope_to: string;
  phases: ConsumptionPhase[];
  totals: ConsumptionBucket;
  excluded_non_article_tx: number;
  per_day: ConsumptionDayRow[];
  per_week: ConsumptionWeekRow[];
  per_phase: ConsumptionPhaseRow[];
  by_category: ConsumptionCategoryRow[];
  by_group: ConsumptionGroupRow[];
  top_articles: ConsumptionTopArticleRow[];
  outliers: ConsumptionOutliers;
}

export interface ConsumptionSourceListItem {
  source: string;
  label: string;
  year: number | null;
  scope_from: string;
  scope_to: string;
}

export interface ConsumptionDatasetSummary {
  id: number;
  name: string;
  year: number;
  festival_from_at: string | null;
  festival_to_at: string | null;
  scope_from: string;
  scope_to: string;
  row_count: number;
  excluded_non_article: number;
  uploaded_at: string;
  uploaded_by_admin_user_id: number | null;
  unmapped_article_count: number;
}

export interface ConsumptionDatasetMappingRow {
  article_name: string;
  category_id: number | null;
  product_group_id: number | null;
  taps: number;
  units: number;
  revenue_cents: number;
}

export interface ConsumptionDatasetDetail extends ConsumptionDatasetSummary {
  mapping: ConsumptionDatasetMappingRow[];
}

export interface ConsumptionMappingUpdateItem {
  article_name: string;
  category_id: number | null;
  product_group_id: number | null;
}

export interface StockDashboardEntry {
  chain_root_id: number;
  name: string;
  expected_qty: number;
  counted_qty: number | null;
  mismatch_qty: number | null;
  sell_price_cents: number;
  buy_price_cents: number | null;
  margin_cents: number | null;
  days_of_cover: number | null;
  is_low_stock: boolean;
}

export interface ErpConfigRow {
  enabled: boolean;
  base_url: string | null;
  auth_type: string | null;
  sync_interval_minutes: number;
  last_sync: string | null;
  last_status: string | null;
  has_credential: boolean;
  erp_row_count: number;
}

// ---- SumUp settlement (add-sumup-settlement) ----
// Mirrors ``app.routes.sumup_settings`` Pydantic models exactly. Card
// payments are referenced server-side by the immutable payment_method
// code ``"card"``; nothing here reaches into the card method by name.

/** Terminal presentation when both QR + reader channels are offered:
 * ``both_live`` arms both at once (first-PAID wins) vs ``user_selects``
 * which makes the payer pick one. Mirrors ``SumupPresentation``. */
export type SumupPresentation = "both_live" | "user_selects";

/** Per-surface direction gate. ``repayment_only`` blocks bank→user
 * payouts (self-service can only ever repay a debt); ``both`` allows the
 * operator surface to move money either way. Mirrors
 * ``SumupDirectionPolicy``. */
export type SumupDirectionPolicy = "repayment_only" | "both";

export type SumupEnvironment = "sandbox" | "live";

/** One row of the extensible per-surface policy matrix (keyed by
 * ``surface``). Mirrors ``SurfacePolicyRow``. */
export interface SumupSurfacePolicyRow {
  surface: string;
  offer_qr: boolean;
  offer_reader: boolean;
  presentation: SumupPresentation;
  allow_partial: boolean;
  min_partial_cents: number;
  require_confirm: boolean;
  reader_id: string | null;
  reader_label: string | null;
  disable_guest_after_full: boolean;
  direction_policy: SumupDirectionPolicy;
  /** Per-surface top-up cap (cents above the debt a live charge may carry the
   * user into credit; only honoured when direction_policy=both). */
  topup_max_cents: number;
  /** When true, a confirmed REGULAR member can't self-pay on this surface
   * (tablet guests + GUEST-typed users are unaffected). */
  guests_only: boolean;
  /** Message shown to a blocked non-guest; null → app default. */
  non_guest_message: string | null;
}

export interface SumupConfigOut {
  enabled: boolean;
  environment: SumupEnvironment;
  merchant_code: string | null;
  settle_timeout_seconds: number;
  result_display_seconds: number;
  return_to_user_page: boolean;
  /** POS: background the card (true) vs a foreground/blocking card flow (false). */
  card_background_enabled: boolean;
  last_test_at: string | null;
  last_test_status: string | null;
  has_secret_key: boolean;
  has_affiliate_key: boolean;
  surfaces: SumupSurfacePolicyRow[];
}

/** Full surface row for the PUT upsert — every behaviour column is
 * required (the UI always sends the whole row). Mirrors
 * ``UpdateSurfacePolicyRequest``. */
export interface UpdateSurfacePolicyRequest {
  surface: string;
  offer_qr: boolean;
  offer_reader: boolean;
  presentation: SumupPresentation;
  allow_partial: boolean;
  min_partial_cents: number;
  require_confirm: boolean;
  reader_id: string | null;
  reader_label: string | null;
  disable_guest_after_full: boolean;
  direction_policy: SumupDirectionPolicy;
  /** Per-surface top-up cap (cents above the debt; only honoured when
   * direction_policy=both). Defaulted server-side (5000) if omitted. */
  topup_max_cents: number;
  /** When true, a confirmed REGULAR member can't self-pay on this surface
   * (tablet guests + GUEST-typed users are unaffected). */
  guests_only: boolean;
  /** Message shown to a blocked non-guest; null → app default. */
  non_guest_message: string | null;
}

/** PUT body. Config scalars are optional (omit to keep). Write-only
 * secrets: ``undefined`` = keep stored, ``""`` = clear, any other string
 * = encrypt + store. Mirrors ``UpdateSumupConfigRequest``. */
export interface UpdateSumupConfigRequest {
  enabled?: boolean;
  environment?: SumupEnvironment;
  merchant_code?: string | null;
  settle_timeout_seconds?: number;
  result_display_seconds?: number;
  return_to_user_page?: boolean;
  card_background_enabled?: boolean;
  secret_key?: string | null;
  affiliate_key?: string | null;
  surfaces?: UpdateSurfacePolicyRequest[];
}

/** Mirrors ``TestResult`` — ``message`` names the environment context. */
export interface SumupTestResult {
  ok: boolean;
  environment: SumupEnvironment;
  message: string;
}

/** Mirrors ``ReaderRow`` (the route's response model). */
export interface SumupReaderRow {
  id: string;
  name: string | null;
  status: string | null;
}

/** Mirrors ``MerchantOption`` — a merchant the key can access. */
export interface SumupMerchantOption {
  merchant_code: string;
  name: string;
  is_test_account: boolean;
  country: string | null;
}

/** Mirrors ``LegRow`` — one payment channel attempt on an intent. */
export interface SumupLegRow {
  channel: string;
  status: string;
  last_error_code: string | null;
}

/** Mirrors ``TransactionRow``. ``stuck`` = paid but not yet settled (the
 * recoverable window the poller retries). */
export interface SumupTransactionRow {
  id: number;
  receipt_no: string;
  surface: string;
  status: string;
  amount_cents: number;
  direction: string;
  last_error_code: string | null;
  last_error: string | null;
  settlement_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  expires_at: string;
  legs: SumupLegRow[];
  stuck: boolean;
}

export interface SumupStuckCountOut {
  count: number;
}

/** Response of ``POST /api/settlements/sumup-reader`` — the primed intent.
 * Mirrors the JSON body returned by ``operator_reader_settle``. The
 * settlement itself is created asynchronously by the poller once the
 * reader leg reaches ``paid``; ``intent_id`` is the handle the caller
 * polls (via ``listSumupTransactions``) for the eventual result. */
export interface SumupReaderSettleResult {
  intent_id: number;
  receipt_no: string;
  amount_cents: number;
  direction: string;
  status: string;
  offered_channels: string[];
  reader_armed: boolean;
  /** Hosted-checkout URL for the customer QR (the both-live online leg). Null
   * if the online leg's URL couldn't be fetched at prime. */
  hosted_checkout_url: string | null;
  /** Live balance at prime (negative = debt). */
  balance_cents: number;
  /** Online methods the QR accepts (card / apple_pay / google_pay / paypal). */
  online_payment_methods: string[];
  allow_partial: boolean;
  min_partial_cents: number;
  /** Top-up bounds: charge in [min_charge_cents, abs_debt_cents + topup_max_cents].
   * Prefill abs_debt_cents (debtor) or min_charge_cents (€0/credit), never max.
   * topup_max_cents is already 0 unless direction_policy=both. */
  min_charge_cents: number;
  topup_max_cents: number;
  abs_debt_cents: number;
  direction_policy: SumupDirectionPolicy;
}

/** Response of the operator status/cancel endpoints — one intent's reconciled
 * state + per-leg statuses (mirrors the kiosk ``_intent_response``). First PAID
 * leg (QR or reader tap) wins and yields ``settlement_id`` via the poller. */
export interface SumupOperatorIntentView {
  intent_id: number;
  status: string;
  amount_cents: number;
  receipt_no: string | null;
  /** Linked settlement once a leg paid + the bridge ran (else null). */
  settlement_id: number | null;
  user_disabled: boolean;
  legs: { channel: string; status: string; hosted_checkout_url: string | null }[];
}

export interface RefillLineRow {
  id: number;
  article_chain_root_id: number;
  qty: number;
  unit_cost_cents: number;
}

export interface RefillRow {
  id: number;
  supplier: string | null;
  invoice_ref: string | null;
  received_at: string;
  comment: string | null;
  operator_id: number | null;
  created_at: string;
  lines: RefillLineRow[];
  total_cost_cents: number;
}

// ---- Procurement (replaces legacy /api/refills) ----

/** Server-side state machine values for a procurement row. Mirrors
 * ``app.models.stock.RefillState``. */
export type ProcurementState = "draft" | "received" | "cancelled";

export interface ProcurementLineRow {
  id: number;
  article_chain_root_id: number;
  qty: number;
  unit_cost_cents: number | null;
  crate_count: number;
  pfand_value_cents_snapshot: number | null;
  crate_pfand_value_cents_snapshot: number | null;
  pack_size_at_write: number | null;
}

/** Why a refill's ``received_at`` floor is what it is. Names the
 * FINALIZED stocktake whose ``counted_at`` is the maximum across all
 * stocktakes covering any article on the refill, and the specific
 * article line that bound the floor. Mirrors
 * ``app.stocktake.floor.ReceivedAtMinReason``. */
export interface ReceivedAtMinReason {
  stocktake_id: number;
  counted_at: string;
  article_id: number;
  article_name: string;
}

/** Identifier for the (single) currently in-progress stocktake. Mirrors
 * ``app.stocktake.floor.StocktakeInProgress`` and is distinct from
 * ``StocktakeInProgressPayload`` (which is the 409 response envelope —
 * carries an additional ``error`` literal). */
export interface StocktakeInProgressInfo {
  stocktake_id: number;
  started_at: string;
}

export interface ProcurementRow {
  id: number;
  state: ProcurementState;
  supplier: string | null;
  invoice_ref: string | null;
  received_at: string | null;
  delivery_date: string | null;
  comment: string | null;
  operator_id: number | null;
  created_at: string;
  updated_at: string;
  lines: ProcurementLineRow[];
  total_cost_cents: number;
  total_pfand_cents: number;
  bill_total_cents: number;
  /** Backdate floor (see openspec/changes/add-procurement-backdate).
   * ``max(counted_at) + 1s`` over FINALIZED stocktakes whose lines
   * cover any article on this refill; ``null`` when no covering
   * stocktake exists. Recomputed on every GET. */
  received_at_min: string | null;
  /** Names the binding stocktake + article so the modal can render
   * "locked by stocktake #N on <article name>". */
  received_at_min_reason: ReceivedAtMinReason | null;
  /** PATCH response-only signal: set to true when a successful backdate
   * landed AND another refill on the same article sits between the old
   * and new ``received_at`` (design D4). Math stays consistent; the
   * modal surfaces a heads-up. Default false on every other response. */
  buy_price_timeline_resort?: boolean;
  /** PATCH response-only: ids of articles whose buy-price timeline is
   * re-sorted by the backdate. Empty on every other response. */
  resort_article_ids?: number[];
}

export interface ProcurementLineInput {
  article_chain_root_id: number;
  qty: number;
  unit_cost_cents?: number | null;
  crate_count?: number | null;
}

export interface CreateProcurementInput {
  state: ProcurementState;
  supplier?: string | null;
  invoice_ref?: string | null;
  delivery_date?: string | null;
  received_at?: string | null;
  comment?: string | null;
  lines: ProcurementLineInput[];
  /** Cash drawer integration (2026-06-04). Only consulted when
   * ``state === "received"``; idempotent server-side. */
  paid_in_cash?: boolean;
}

export interface UpdateProcurementHeaderInput {
  supplier?: string | null;
  invoice_ref?: string | null;
  delivery_date?: string | null;
  comment?: string | null;
  /** Backdate path (see openspec/changes/add-procurement-backdate). Only
   * accepted when the procurement is in ``received`` state and subject
   * to the stocktake-derived floor + global in-progress block; rejected
   * with HTTP 409 ``received_at_below_floor`` / ``stocktake_in_progress``
   * / ``received_at_only_editable_on_received`` otherwise. */
  received_at?: string | null;
}

/** Request body for ``POST /api/procurement/received-at-floor`` — the
 * preflight that backs the create-procurement modal where lines are
 * not yet persisted. */
export interface ReceivedAtFloorRequest {
  article_ids: number[];
}

/** Response from ``POST /api/procurement/received-at-floor``. Mirrors
 * ``app.routes.procurement.ReceivedAtFloorResponse``. */
export interface ReceivedAtFloorResponse {
  received_at_min: string | null;
  received_at_min_reason: ReceivedAtMinReason | null;
  /** Non-null = ANY stocktake is in-progress globally (design D2). The
   * modal must disable the picker entirely and deep-link to the count. */
  stocktake_in_progress: StocktakeInProgressInfo | null;
}

/** Possible inline rejection codes the backdate-impact preview surfaces
 * so the modal can show the same reason a PATCH would, without forcing
 * a doomed PATCH attempt. */
export type BackdateImpactErrorCode =
  | "received_at_only_editable_on_received"
  | "stocktake_in_progress"
  | "received_at_below_floor";

/** Response from ``GET /api/procurement/{id}/backdate-impact?to=<iso>``.
 * Mirrors ``app.routes.procurement.BackdateImpactResponse``. */
export interface BackdateImpactResponse {
  articles_affected: number;
  sales_in_window: number;
  pl_bucket_old: string | null;
  pl_bucket_new: string | null;
  buy_price_timeline_resort: boolean;
  resort_article_ids: number[];
  /** When non-null, the proposed datetime would be rejected by PATCH for
   * this reason — the modal renders the same rejection inline without
   * actually attempting the PATCH. */
  error_code: BackdateImpactErrorCode | null;
  received_at_min: string | null;
  received_at_min_reason: ReceivedAtMinReason | null;
  stocktake_in_progress: StocktakeInProgressInfo | null;
}

/** 409 body when any PATCH (line or state) lands while a stocktake is
 * in progress; drives the non-dismissable banner on the detail page. */
export interface StocktakeInProgressPayload {
  error: "stocktake_in_progress";
  stocktake_id: number;
  started_at: string;
}

export interface SupplierSuggestion {
  display_name: string;
  canonical_key: string;
  most_recent_use: string;
}

export interface StocktakeRow {
  id: number;
  started_at: string;
  // counted_at + updated_at land with the Phase-2 backend (§7); the
  // shape stays nullable for backwards compatibility with the legacy
  // detail-modal data path.
  counted_at: string | null;
  finalized_at: string | null;
  operator_id: number | null;
  scope: "full" | "partial";
  comment: string | null;
  line_count: number;
  finalized: boolean;
  /** ISO timestamp the optimistic-concurrency PATCHes / DELETE round-trip
   * via If-Match. Null on legacy rows that pre-date the column. */
  updated_at: string | null;
}

export interface StocktakeLineRow {
  article_chain_root_id: number;
  counted_qty: number;
  /** 0044: post-finalize soft-revert marker. Non-null means the line
   * is an economically inactive ghost row (its linked
   * stock_adjustment + Stock-loss expense were hard-deleted by the
   * revert cascade). UI should render it grayed out with a
   * "reverted" badge and hide the ↶ / × buttons. */
  reverted_at?: string | null;
  /** Verify-fix MODERATE #7: state of the linked Stock-loss Expense
   * at read time. Only populated on detail (GET /stocktakes/{id})
   * for post-finalize lines; null elsewhere. Drives the revert
   * confirm-modal's copy. */
  loss_expense_state?: "live" | "soft_deleted" | "none" | null;
  /** The moment the operator first set ``counted_qty`` for this line.
   * Set on first PATCH/scan, cascaded from a header PATCH on
   * ``stocktake.counted_at``, or explicitly re-stamped via
   * ``POST /lines/{root}/restamp-counted-at`` after a re-walk.
   * Legacy rows from before migration 0041 carry NULL and fall back
   * to ``stocktake.counted_at`` for finalize/cascade math. */
  counted_at?: string | null;
  // Frozen at finalize-time; null on in-progress lines.
  expected_qty_at_finalize?: number | null;
  buy_price_cents_at_finalize?: number | null;
  pfand_value_cents_at_finalize?: number | null;
  crate_pfand_value_cents_at_finalize?: number | null;
  /** 0051: structured term list from
   * ``app/articles/count_expression.py``. Three states (see the
   * "Lazy Synthesis" spec requirement):
   *
   *   - ``null`` or ``undefined`` → uncounted line
   *   - ``[]`` → counted as zero (operator explicitly committed empty)
   *   - non-empty array → normal / legacy-synthesized state. Legacy
   *     synthesis adds a ``sealed: true`` flag on the single loose
   *     term; the SPA renders sealed chips non-deletable. */
  count_expression?: import("../utils/countExpression").Term[] | null;
  /** §5 (add-pos-recipes-and-cups): measured contents of the OPEN container at
   * count time, in base units (mL for LITERS, g for GRAMS). ``null`` when not
   * captured (non-volumetric article or simply not measured). Seeds the
   * open-container overlay's inter-count window on finalize. */
  measured_open_base_units?: number | null;
}

export interface StocktakeDetail extends StocktakeRow {
  lines: StocktakeLineRow[];
}

export interface StocktakeBootstrap {
  stocktake_id: number;
  snapshot_at: string;
  /** Keyed by ``String(chain_root_id)`` since JSON object keys are
   * strings. Callers should `Number()` the key before lookups.
   * Anchored at each line's first-stamp ``counted_at`` (or the
   * stocktake header / finalized_at fallback chain) for lines that
   * have one; legacy lines and uncounted articles fall back to NOW. */
  expected_qty_at_start: Record<string, number>;
  /** Same map but anchored at NOW for every catalog article. Drives
   * the "↻ re-walk" affordance: when ``expected_qty_now[root] !==
   * expected_qty_at_start[root]``, the world has moved on since the
   * operator's first-stamp moment. The drift line + button render
   * off this delta. */
  expected_qty_now: Record<string, number>;
}

/** Response from ``POST /api/stocktakes/{id}/scan``. Either the barcode
 * resolved (returns the new counted_qty + qty applied) or it didn't
 * (frontend opens the unknown-barcode modal). */
export type ScanResponse =
  | {
      resolved: true;
      chain_root_id: number;
      counted_qty: number;
      qty_applied: number;
      idempotent_replay: boolean;
    }
  | { resolved: false; barcode: string };

export interface FinalizeBlocker {
  chain_root_id: number;
  reason: string;
}

export interface FinalizeWarningRefill {
  id: number;
  supplier: string | null;
  delivery_date: string | null;
  line_count: number;
}

export interface FinalizeWarning {
  code: string;
  message: string;
  refills: FinalizeWarningRefill[];
}

export interface FinalizeLine {
  chain_root_id: number;
  counted_qty: number;
  expected_qty: number;
  mismatch_qty: number;
  buy_price_cents: number | null;
  pfand_value_cents: number;
  crate_pfand_value_cents: number;
  proposed_delta_qty: number;
  proposed_stock_loss_value_cents: number;
  /** Always 0 from 2026-06-04: Pfand stays on-site; not a
   * stocktake-loss event. Field retained for wire-format stability. */
  proposed_pfand_value_cents: number;
  /** 0051: attribution split. ``loss_qty`` is the shortfall that goes
   * to Stock-loss (unexplained); ``broken_qty`` is the operator-
   * declared breakage capped at the shortfall. Both >= 0; sum equals
   * ``max(0, expected_qty − counted_qty)`` by construction. */
  attribution_loss_qty: number;
  attribution_broken_qty: number;
  /** NEGATIVE cents per accounting.md §2 (bank-statement-signed).
   * Mirror of ``proposed_stock_loss_value_cents`` for the broken row. */
  proposed_stock_broken_value_cents: number;
}

/** Per-line soft warning emitted by the cascade. Surface as a row
 * badge on the preview screen. */
export interface FinalizeSoftWarning {
  chain_root_id: number;
  /** Machine-readable code. Currently the only one is
   * ``buy_price_predates_anchor_fallback`` (a line whose per-line
   * counted_at is BEFORE the article's earliest known ArticleBuyPrice
   * — finalize used the earliest known price). */
  code: string;
  detail?: string | null;
}

export interface FinalizeProposal {
  lines: FinalizeLine[];
  blockers: FinalizeBlocker[];
  /** Soft, non-blocking nudges (e.g. draft procurements that look like
   * they're already on the shelf). The finalize button stays enabled
   * even when warnings are present. */
  warnings: FinalizeWarning[];
  /** Per-line soft warnings from the cascade (buy_price fallback etc.) */
  soft_warnings: FinalizeSoftWarning[];
  total_stock_loss_cents: number;
  /** Always 0 from 2026-06-04: Pfand stays on-site; not a
   * stocktake-loss event. Field retained for wire-format stability. */
  total_pfand_cents: number;
  adjustment_count: number;
  /** 0051: parallel total for the Stock-broken expense rows. NEGATIVE
   * (outflow). |total_stock_loss_cents| + |total_stock_broken_cents|
   * equals what the pre-0051 single Stock-loss total would have been
   * for the same finalize (aggregate-parity guarantee). */
  total_stock_broken_cents: number;
}

/** Live state of the linked Stock-loss expense row at preview time.
 * ``auto`` — row exists with cascade-computed value_cents (no operator edit).
 * ``operator_edited`` — row exists but value_cents diverges from the cascade
 *   formula; apply will OVERWRITE the manual edit.
 * ``deleted_by_operator`` — row missing (soft- or hard-deleted); apply will
 *   RECREATE it if new_loss_qty > 0.
 * ``none`` — no loss row was expected at finalize (frozen mismatch >= 0). */
export type ReanchorLossState =
  | "auto"
  | "operator_edited"
  | "deleted_by_operator"
  | "none";

/** Per-line diff returned by GET /stocktakes/{id}/reanchor-preview. */
export interface ReanchorLineDiffRow {
  chain_root_id: number;
  article_name: string | null;
  frozen_expected_qty: number;
  new_expected_qty: number;
  counted_qty: number;
  frozen_mismatch: number;
  new_mismatch: number;
  frozen_adjustment_delta: number;
  new_adjustment_delta: number;
  frozen_loss_qty: number;
  new_loss_qty: number;
  /** What the linked Stock-loss expense row actually looks like in the
   * DB right now — drives the apply-step warning copy. */
  frozen_loss_state: ReanchorLossState;
  /** Current value_cents on the live Stock-loss row (negative). Null
   * when the row is missing or no loss was ever expected. */
  frozen_loss_value_cents: number | null;
  /** What value_cents the cascade will write (negative). Null when
   * new_mismatch >= 0 (no loss row will be written). */
  new_loss_value_cents: number | null;
  /** Round-2 audit MODERATE #13+#14: bp refresh surfaced per row.
   * frozen_buy_price_cents is the snapshot captured at finalize;
   * new_buy_price_cents is what the cascade WILL use (live current,
   * with earliest-known soft fallback). */
  frozen_buy_price_cents?: number | null;
  new_buy_price_cents?: number | null;
  is_qty_stale?: boolean;
  is_bp_stale?: boolean;
  is_stale: boolean;
  is_latest_for_root: boolean;
  /** ["no_buy_price"] etc. — non-empty means apply will skip the line. */
  blockers: string[];
}

export interface ReanchorPreviewResponse {
  stocktake_id: number;
  finalized_at: string;
  stale_lines: ReanchorLineDiffRow[];
  /** Stale but cascade refuses to fire (e.g. missing buy_price for a
   * line that would land on a negative new_mismatch). Shown disabled. */
  blocked_lines: ReanchorLineDiffRow[];
  /** Stale but this stocktake is not the latest one for the chain root —
   * older stocktakes can't be re-anchored without breaking the layered
   * adjustment chain. Shown disabled. */
  locked_lines: ReanchorLineDiffRow[];
  /** SHA-256 of the per-line numbers shown in this preview. Echo
   * back on apply via ReanchorApplyRequest.preview_signature; the
   * server 409s if live state shifted between modal-open and apply
   * (round-2 audit MODERATE #12). */
  preview_signature: string;
}

export interface ReanchorApplyResponse {
  stocktake_id: number;
  applied_chain_root_ids: number[];
}

export interface ExpenseRow {
  id: number;
  date: string;
  category_id: number;
  comment: string | null;
  /** Non-Pfand cash portion (the "subtotal"). Sign encodes direction:
   * negative for cash out (goods bought / paid Pfand), positive for cash
   * in (refunds / Pfand returns). May be zero on Pfand-only rows. */
  value_cents: number;
  /** INDEPENDENT Pfand cash portion — NOT a subset of ``value_cents``
   * (refactor 2026-06-04). Null means "no Pfand component". When both
   * ``value_cents`` and ``pfand_value_cents`` are non-zero they MUST
   * share a sign — that's the only invariant. A row with value=0 and
   * pfand non-zero is a valid "Pfand-only" row. */
  pfand_value_cents: number | null;
  /** VAT classification; null = no rate set (operator picks, incl. a custom
   *  0% "no receipt" rate). Prices stay gross; net derived via utils/vat. */
  vat_rate_id: number | null;
  receipt_path: string | null;
  linked_stocktake_id: number | null;
  linked_stocktake_line_chain_root_id: number | null;
  /** Set when the expense was attached to a procurement (refill) — drives
   * the "Expenses & Pfand returns" section on the procurement detail
   * page. Null on standalone expenses. */
  linked_procurement_id: number | null;
  /** Server-DERIVED (not a DB column): true when ``category_id`` is one of
   * the three drawer-bookkeeping categories (Cash drawer / Cash drawer
   * opening / Cash variance). Drives the derived "Cash drawer" type + the
   * matching filter in the Linked column. */
  is_drawer_flow: boolean;
  created_at: string;
  updated_at: string;
}

/** Wire shape of ``GET /api/dashboard/venue-summary`` — drives the
 * Expenses page net-cash position summary rows. All three scalars are
 * absolute/signed integer cents:
 *
 * * ``strichliste_sales_cents`` — POSITIVE total of drinks dispensed
 *   (ABS(amount) over transactions with article_id NOT NULL).
 * * ``procurement_goods_cents`` — NEGATIVE cash outflow on received refills.
 * * ``procurement_pfand_cents`` — NEGATIVE cash outflow on received Pfand.
 * * ``potential_mismatch_revenue_cents`` — POSITIVE (always ≥0)
 *   speculative figure: Σ positive stock_adjustment deltas × sell price.
 *   Off by default in the UI (speculative).
 * * ``potential_revenue_on_stock_cents`` — POSITIVE (always ≥0)
 *   current stock × sell price across all catalog articles. Off by
 *   default in the UI (speculative).
 */
export interface VenueSummary {
  strichliste_sales_cents: number;
  procurement_goods_cents: number;
  procurement_pfand_cents: number;
  potential_mismatch_revenue_cents: number;
  potential_revenue_on_stock_cents: number;
}

/** Wire shape of ``GET /api/dashboard/pfand-balance`` (refactor-stock-workspace
 * §14.3). All four scalars are non-negative integer cents.
 *
 * * ``received_cents`` — Pfand booked-in via refill_line snapshots over
 *   received refills.
 * * ``returned_cents`` — magnitude of positive Pfand-category expenses
 *   (cash inflow from returned empties).
 * * ``paid_out_cents`` — magnitude of negative Pfand-category expenses
 *   (informational only — NOT subtracted from the net).
 * * ``net_outstanding_cents`` — ``received_cents - returned_cents``.
 */
export interface PfandBalanceResponse {
  received_cents: number;
  returned_cents: number;
  paid_out_cents: number;
  net_outstanding_cents: number;
}

/** One row in the outstanding-procurement breakdown — a received refill
 * that has not yet been flipped out of the outstanding set by a
 * Cash-drawer category expense linking back to it. */
export interface OutstandingProcurementRefillRow {
  refill_id: number;
  supplier_name: string | null;
  total_cents: number;
  received_at: string | null;
}

/** Wire shape of ``GET /api/dashboard/outstanding-procurement`` —
 * accounting.md §6.3 "owed to corporate" scalar. ``total_cents`` is the
 * Σ over received refills that have no linked Cash-drawer expense;
 * ``refill_count`` mirrors ``refills.length``. */
export interface OutstandingProcurementResponse {
  total_cents: number;
  refill_count: number;
  refills: OutstandingProcurementRefillRow[];
}

/** One per-person arrival or departure event on a given day. ``real_name``
 * is the canonical full name from ``user_identity`` (may be NULL — the
 * frontend falls back to ``nickname``); ``sort_first_name`` is pre-computed
 * server-side (lowercased first whitespace-separated token of ``real_name``,
 * or the lowercased ``nickname``) so the client can keep the same intra-day
 * ordering without locale-dependent JS collation. */
export interface ArrivalEvent {
  strichliste_user_id: number;
  real_name: string | null;
  nickname: string;
  sort_first_name: string;
}

/** One day in the arrivals/departures schedule. ``date`` is an ISO
 * ``YYYY-MM-DD`` (Pydantic ``date`` serialisation). Both lists may be
 * empty — the widget still renders the day row so operators see the
 * contiguous date range. */
export interface ArrivalsDay {
  date: string;
  arrivals: ArrivalEvent[];
  departures: ArrivalEvent[];
}

/** Wire shape of ``GET /api/dashboard/arrivals``. ``days`` is a contiguous
 * list of every calendar day from the first arrival to the last departure
 * (inclusive), or ``[]`` when no roster rows have been imported yet.
 * ``today`` is the server's notion of today (UTC) — used by the frontend
 * for the auto-scroll anchor + the "today" row highlight. */
export interface ArrivalsResponse {
  days: ArrivalsDay[];
  today: string;
}

/** One row in the unified ``/expenses/timeline`` feed. Mirrors
 * ``app.routes.expenses.TimelineRow``. ``id`` is the natural row id for
 * ``real``/``drawer``/``correction``/``settlement``/``procurement`` and
 * a synthetic epoch-day id for ``sales`` (so the React key is stable
 * across requests for the same bucket).
 *
 * Sign conventions vary by source — clients should NOT assume a uniform
 * sign and instead render ``value_cents`` with its natural sign:
 * * ``real``        — same as ``ExpenseRow.value_cents`` (operator-signed).
 * * ``drawer``      — same as above; drawer-bookkeeping rows.
 * * ``procurement`` — NEGATIVE (cash out at receipt time).
 * * ``sales``       — POSITIVE (cash in from drinks dispensed).
 * * ``correction``  — POSITIVE magnitude (reduces prior sales income).
 * * ``settlement``  — SIGNED by direction (user_to_bank = +, bank_to_user = −).
 */
export interface TimelineRow {
  source:
    | "real"
    | "drawer"
    | "procurement"
    | "sales"
    | "correction"
    | "settlement"
    | "voucher";
  id: number;
  date: string;
  /** ISO 8601 timestamp; used by the server for chronological merging. */
  sort_key: string;
  value_cents: number;
  pfand_value_cents: number | null;
  /** Only set for ``real`` / ``drawer`` rows. */
  category_id: number | null;
  category_name: string | null;
  comment: string | null;
  /** ``{type, id}`` payload the UI can map to a deep link. */
  link: { type: string; id: number } | null;
  is_drawer_flow: boolean;
  /** VAT classification — only set on ``real`` rows (the editable ones);
   * null on virtual rows + on real rows with no rate assigned. Prices
   * stay gross; net is derived via utils/vat. */
  vat_rate_id: number | null;
}

/** Aggregate totals returned alongside the timeline rows. All cents.
 * Conventions:
 * * ``value_p_and_l_cents`` / ``pfand_net_position_cents`` are
 *   INCOME-positive (positive = profit / venue holds deposits).
 * * ``open_user_debts_cents`` is the lifetime ABS sum of negative
 *   balances on active strichliste users (always non-negative).
 * * ``corporate_funding_cents`` is the ABS magnitude of
 *   corporate-funding-category Expense rows in the window.
 * * ``mismatch_revenue_cents`` / ``revenue_on_stock_cents`` are the
 *   speculative scalars reused from ``venue_summary``.
 */
export interface TimelineSums {
  value_p_and_l_cents: number;
  pfand_net_position_cents: number;
  open_user_debts_cents: number;
  corporate_funding_cents: number;
  mismatch_revenue_cents: number;
  revenue_on_stock_cents: number;
  /** 0051: ABS magnitudes from the window — flip negative when rendering. */
  stock_loss_cents: number;
  stock_broken_cents: number;
  volunteer_comp_cents: number;
}

export interface TimelineResponse {
  rows: TimelineRow[];
  sums: TimelineSums;
}

export interface ExpenseCategoryRow {
  id: number;
  name: string;
  code: string;
  system_seeded: boolean;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EstimationExclusionRow {
  id: number;
  label: string | null;
  from_at: string;
  to_at: string;
  source: "manual" | "festival";
  created_at: string;
  updated_at: string;
}

export interface OperationalWindowRow {
  buildup_start_date: string | null;
  takedown_end_date: string | null;
}

export interface UserScannerConfigRow {
  prefix: string;
  numeric: boolean;
  min_len: number;
  max_len: number | null;
}

// ---- Cash drawer ----

export type CashActivityKind =
  | "settlement"
  | "expense"
  | "variance"
  | "opening"
  | "procurement_payment";

export interface CashActivityLink {
  type: "settlement" | "expense" | "procurement";
  id: number;
}

export interface CashActivityRow {
  when: string;
  /** Signed: positive = INTO the drawer; negative = OUT of the drawer.
   *  For expenses this is value_cents + pfand_value_cents combined. */
  flow_cents: number;
  /** Signed Pfand portion of this event (accounting allocation only —
   *  the physical cash isn't sortable). */
  pfand_flow_cents: number;
  kind: CashActivityKind;
  description: string;
  link: CashActivityLink | null;
}

export interface CashDrawerLastCount {
  counted_cents: number;
  recorded_at: string;
  /** Signed delta at the time of count. */
  variance_cents: number;
  comment: string | null;
}

export interface CashDrawerState {
  /** Computed total cash expected in the drawer (value + pfand combined). */
  expected_balance_cents: number;
  /** Computed Pfand-allocated portion of the expected balance — a derived
   *  accounting figure, NOT something the operator counts separately. */
  expected_pfand_cents: number;
  /** Authoritative server-side count of currently-active opening rows.
   *  0 = drawer not yet opened; 1 = normal; >1 = duplicate openings exist
   *  (operator should review the Expenses page and soft-delete the extras). */
  active_opening_count: number;
  last_count: CashDrawerLastCount | null;
  recent_activity: CashActivityRow[];
}

export interface CashDrawerSnapshot {
  at: string;
  expected_balance_cents: number;
  expected_pfand_cents: number;
}

export interface CashDrawerMonthEndRow {
  month: string; // "YYYY-MM"
  at: string;
  expected_balance_cents: number;
  expected_pfand_cents: number;
}

/** Mirrors ``app.routes.cash_drawer.GuestSettleCandidate`` — one row per
 * strichliste user eligible for the cash-drawer "Barbezahlung" quick-settle
 * bubble selector. "Guest" = any strichliste user whose admin user_type is
 * **not** ``REGULAR`` or ``BANK``. ``balance_cents`` uses strichliste's
 * sign convention: negative = guest owes the bar, positive = bar owes
 * the guest. */
export interface GuestSettleCandidate {
  strichliste_user_id: number;
  nickname: string;
  balance_cents: number;
  disabled: boolean;
}

